/* Common Imports */

import { Box, styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { formatBhkValue } from "../../../../../lib/PrepareConfigString";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	UpdateConfigAndAreaThunk,
	setDataBeforeEditThunk,
	setPropertySnackbarThunk,
	setSectionToEditThunk,
	setSinglePropertyAboutLocationThunk,
	setSinglePropertyAboutThunk,
	setSinglePropertyAgeThunk,
	setSinglePropertyAmenitiesThunk,
	setSinglePropertyAvailabiltyThunk,
	setSinglePropertyCategoryTypeThunk,
	setSinglePropertyDescriptionThunk,
	setSinglePropertyDeveloperNameThunk,
	setSinglePropertyEstablishedYearThunk,
	setSinglePropertyFloorNoThunk,
	setSinglePropertyFurnishingThunk,
	setSinglePropertyLandAreaThunk,
	setSinglePropertyLandFacingThunk,
	setSinglePropertyMaxBalconiesThunk,
	setSinglePropertyMaxBathroomsThunk,
	setSinglePropertyMaxBedroomsThunk,
	setSinglePropertyMaxCarpetAreaThunk,
	setSinglePropertyMaxFloorsThunk,
	setSinglePropertyMaxPriceThunk,
	setSinglePropertyMaxSuperBuiltUpAreaThunk,
	setSinglePropertyMinBalconiesThunk,
	setSinglePropertyMinBathroomsThunk,
	setSinglePropertyMinBedroomsThunk,
	setSinglePropertyMinCarpetAreaThunk,
	setSinglePropertyMinPriceThunk,
	setSinglePropertyMinSuperBuiltUpAreaThunk,
	setSinglePropertyNoOfSeatsThunk,
	setSinglePropertyNoOfUnitsThunk,
	setSinglePropertyOtherPlotDimensionsThunk,
	setSinglePropertyOverviewThunk,
	setSinglePropertyPlotDimensionsThunk,
	setSinglePropertyPossessionDateThunk,
	setSinglePropertySecurityDepositThunk,
	setSinglePropertySubTypeThunk,
	setSinglePropertyTagsThunk,
	setSinglePropertyTransactionTypeThunk,
	setSinglePropertyTypeThunk,
	setSinglePropertyUnitFacingThunk,
	setSinglePropertyVicinityThunk,
} from "../../../../../redux-magic/store";

/* Component Imports */

import {
	Alert,
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Skeleton,
	Snackbar,
	SvgIcon,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Icon Imports */

import {
	Add,
	AddBoxOutlined,
	AspectRatioOutlined,
	BalconyOutlined,
	BathtubOutlined,
	ChairOutlined,
	Close,
	CorporateFareRounded,
	DeleteOutline,
	EditOutlined,
	EventSeatOutlined,
	KingBedOutlined,
	StairsOutlined,
} from "@mui/icons-material";
import SuperBuiltAreaIcon from "../../../../../public/images/icons/config/super_built_up_area.svg";
import LandAreaIcon from "../../../../../public/images/icons/config/total_land_area.svg";
import { confirmSendForReview, skipNextSendForReviewConfirm } from "../../SendForReviewConfirm";
import { confirmDiscardChanges, useEditSnapshot } from "../../SectionDiscardConfirm";

/* Styled Components */

const PaperContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	gap: "0.5rem",
	overflow: "auto",
	boxShadow: "none",
	background: theme.palette.background.paper,
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		width: "50%",
		padding: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "0rem 0rem 0.25rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 0.5rem 0rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const TextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.6rem",
	width: "100%",
	padding: "0rem 0rem 0rem 0rem",
}));

const CustomText = styled(Typography)(({ theme }) => ({
	whiteSpace: "pre",
	fontWeight: "400",
	fontSize: "0.825rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	// marginTop: "2.8rem",
	// [theme.breakpoints.down("sm")]: {
	// 	marginTop: "3.8rem",
	// },
}));

const DetailsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	width: "100%",
}));

const CustomChip = styled(Button, {
	shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
	textAlign: "center",
	borderRadius: "8px",
	border: isSelected
		? "1px solid var(--Primary-New-Dark, #B05911)"
		: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
	backgroundColor: isSelected ? "#FFEBDA" : "#FFFFFF",
	color: isSelected ? "#B05911" : "inherit",
	fontWeight: 400,
	fontSize: "14px",
	padding: "0.5rem",
	"@media (pointer: fine)": {
		"&:hover": {
			backgroundColor: isSelected ? "#FFEBDA" : "#F5F5F5",
			border: isSelected
				? "1px solid var(--Primary-New-Dark, #B05911)"
				: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
		},
	},
	"@media (pointer: coarse)": {
		"&:hover": {
			backgroundColor: isSelected ? "#FFEBDA" : "#FFFFFF",
			border: isSelected
				? "1px solid var(--Primary-New-Dark, #B05911)"
				: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
		},
	},
	"&:active": {
		backgroundColor: isSelected ? "#FFEBDA" : "#FFFFFF",
		border: isSelected
			? "1px solid var(--Primary-New-Dark, #B05911)"
			: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
	},
	"&:focus": {
		backgroundColor: isSelected ? "#FFEBDA" : "#FFFFFF",
		border: isSelected
			? "1px solid var(--Primary-New-Dark, #B05911)"
			: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
	},
}));

const Text = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	fontWeight: "400",
	lineHeight: "1rem",
	padding: "3px 6px",
	textTransform: "capitalize",
	color: theme.palette.text.primary,
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

interface Area {
	label: string;
	enableLabel: boolean;
	land_area: {
		min: { ft: number; mt: number; acre: number };
		max: { ft: number; mt: number; acre: number };
	};
	super_built_up_area: {
		min: { ft: number; mt: number; acre: number };
		max: { ft: number; mt: number; acre: number };
	};
	carpet_area: {
		min: { ft: number; mt: number; acre: number };
		max: { ft: number; mt: number; acre: number };
	};
	karab_area: {
		min: { ft: number; mt: number; acre: number };
		max: { ft: number; mt: number; acre: number };
	};
}

interface BHK {
	min: number;
	max: number;
	label: string;
	enableLabel: boolean;
}

const ConfigAndAreaCard = ({
	propertyData,
	property_furnishing_status,
	property_plot_dimensions,
	other_plot_dimensions,
	property_area,
	property_bhk,
	property_bathroom,
	property_balconies,
	property_floors,
	property_floor_no,
	property_no_of_units,
	property_no_of_seats,
	property_created_by,
	property_id,
	dispatch,
	session,
	isEditor,
	profile_context,
	loading,
	section_to_edit,
	data_before_edit,
	config_area_snackbar,
	screenType,
}: {
	propertyData: any;
	property_furnishing_status: Array<string>;
	property_plot_dimensions: Array<string>;
	other_plot_dimensions: Array<string>;
	property_area: Area;
	property_bhk: BHK;
	property_bathroom: BHK;
	property_balconies: BHK;
	property_floors: BHK;
	property_floor_no: BHK;
	property_no_of_units: BHK;
	property_no_of_seats: BHK;
	property_created_by: LocalPropertyInterface["created_by"];
	property_id: ObjectId | string;
	dispatch: Function;
	session: any;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	loading: boolean;
	section_to_edit: string;
	data_before_edit: any;
	config_area_snackbar: boolean;
	screenType: string;
}) => {
	const theme = useTheme();

	/*

  & Let's declare the router. Here we are using the useRouter hook from nextjs.

  & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

  */

	const router = React.useRef(useRouter()).current;

	const localLang = {
		configAndArea:
			router.locale === "kn-IN"
				? "ಯೋಜನೆ ಮತ್ತು ಅಳತೆ"
				: router.locale === "ar-AE"
					? "التكوين والمساحة"
					: "Configuration & Area",
		snackValue:
			router.locale === "kn-IN"
				? "ಪ್ರಾಪರ್ಟಿ ಕಾನ್ಫಿಗರೇಶನ್ಸ್ ಯಶಸ್ವಿಯಾಗಿ ಅಪ್ಡೇಟ್ ಆಗಿದೆ"
				: router.locale === "ar-AE"
					? "تم تحديث تكوينات العقار بنجاح"
					: "Property configurations updated successfully",
		furnishing: router.locale === "kn-IN" ? "ಫರ್ನಿಶಿಂಗ್" : router.locale === "ar-AE" ? "التأثيث" : "Furnishing",
		bhk: router.locale === "kn-IN" ? "ಬಿಎಚ್ಕೆ" : router.locale === "ar-AE" ? "عدد الغرف" : "BHK",
		bathrooms: router.locale === "kn-IN" ? "ಬಾತ್ ರೂಮ್ಸ್" : router.locale === "ar-AE" ? "عدد الحمامات" : "Bathrooms",
		balconies: router.locale === "kn-IN" ? "ಬಾಲ್ಕನಿಗಳು" : router.locale === "ar-AE" ? "عدد الشرفات" : "Balconies",
		noOfSeats: router.locale === "kn-IN" ? "ಸೀಟುಗಳ ಸಂಖ್ಯೆ" : router.locale === "ar-AE" ? "عدد المقاعد" : "No of seats",
		totalNoOfUnits:
			router.locale === "kn-IN"
				? "ಯುನಿಟ್ಗಳ ಸಂಖ್ಯೆ"
				: router.locale === "ar-AE"
					? "إجمالي عدد الوحدات"
					: "Total No of units",
		totalNoOfFloors:
			router.locale === "kn-IN"
				? "ಒಟ್ಟು ಮಹಡಿಗಳ ಸಂಖ್ಯೆ"
				: router.locale === "ar-AE"
					? "إجمالي عدد الطوابق"
					: "Total No of floors",
		PlotDimensions:
			router.locale === "kn-IN"
				? "ಪ್ಲಾಟ್ ಡೈಮೆನ್ಷನ್ಸ್"
				: router.locale === "ar-AE"
					? "أبعاد قطعة الأرض"
					: "Plot dimensions",
		totalLandArea:
			router.locale === "kn-IN"
				? "ಒಟ್ಟು ಭೂಮಿಯ ವಿಸ್ತೀರ್ಣ"
				: router.locale === "ar-AE"
					? "إجمالي مساحة الأرض"
					: "Total land area",
		plotArea:
			router.locale === "kn-IN"
				? propertyData.property_type.includes("Plot") ||
					propertyData.property_type.includes("Farm Plot") ||
					propertyData.property_type.includes("Plot (Commercial)")
					? "ಪ್ಲಾಟ್ ಏರಿಯಾ"
					: "ಎಸ್.ಬಿ.ಎ"
				: router.locale === "ar-AE"
					? propertyData.property_type.includes("Plot") ||
						propertyData.property_type.includes("Farm Plot") ||
						propertyData.property_type.includes("Plot (Commercial)")
						? "مساحة قطعة الأرض"
						: "مساحة البناء الإجمالية"
					: propertyData.property_type.includes("Plot") ||
						  propertyData.property_type.includes("Farm Plot") ||
						  propertyData.property_type.includes("Plot (Commercial)")
						? "Plot area"
						: "SBA",
		update: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "تحديث" : "Update",
		furnished: router.locale === "kn-IN" ? "ಫರ್ನಿಶ್ಡ್" : router.locale === "ar-AE" ? "مفروشة" : "Furnished",
		semi_furnished:
			router.locale === "kn-IN" ? "ಸೆಮಿ ಫರ್ನಿಶ್ಡ್" : router.locale === "ar-AE" ? "شبه مفروشة" : "Semi Furnished",
		unfurnished: router.locale === "kn-IN" ? "ಅನ್ಫರ್ನಿಶ್ಡ್" : router.locale === "ar-AE" ? "غير مفروشة" : "Unfurnished",
		bedrooms: router.locale === "kn-IN" ? "ಬೆಡ್ರೂಮ್ಗಳು" : router.locale === "ar-AE" ? "عدد الغرف" : "Bedrooms",
		minimum_bedooms:
			router.locale === "kn-IN"
				? "ಕನಿಷ್ಠ ಬೆಡ್ರೂಮ್ಗಳು"
				: router.locale === "ar-AE"
					? "الحد الأدنى لعدد الغرف"
					: "Minimum bedrooms",
		maximum_bedrooms:
			router.locale === "kn-IN"
				? "ಗರಿಷ್ಠ ಬೆಡ್ರೂಮ್ಗಳು"
				: router.locale === "ar-AE"
					? "الحد الأقصى لعدد الغرف"
					: "Maximum bedrooms",
		minimum_bathrooms:
			router.locale === "kn-IN"
				? "ಕನಿಷ್ಠ ಬಾತ್ ರೂಮ್ಗಳು"
				: router.locale === "ar-AE"
					? "الحد الأدنى لعدد الحمامات"
					: "Minimum bathrooms",
		maximum_bathrooms:
			router.locale === "kn-IN"
				? "ಗರಿಷ್ಠ ಬಾತ್ ರೂಮ್ಗಳು"
				: router.locale === "ar-AE"
					? "الحد الأقصى لعدد الحمامات"
					: "Maximum bathrooms",
		minimum_value_is_more_than_maximum_value:
			router.locale === "kn-IN"
				? "ಮೌಲ್ಯ ಗರಿಷ್ಠ ಬೆಲೆಯಿಂದ ಕಡಿಮೆಯಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "القيمة أكبر من السعر الأقصى"
					: "Min value is more than max value",
		maximum_value_is_less_than_minimum_value:
			router.locale === "kn-IN"
				? "ಮೌಲ್ಯ ಕನಿಷ್ಠ ಬೆಲೆಯಿಂದ ಕಡಿಮೆಯಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "القيمة أقل من السعر الأدنى"
					: "Max value is less than min value",
		enter_a_valid_number:
			router.locale === "kn-IN"
				? "ಒಂದು ಮಾನ್ಯ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ"
				: router.locale === "ar-AE"
					? "أدخل رقمًا صالحًا"
					: "Invalid number",
		select_at_least_one_option:
			router.locale === "kn-IN"
				? "ಕನಿಷ್ಠವಾಗಿ ಒಂದು ಪ್ಲಾಟ್ ಮುಟ್ಟಿಸಿ"
				: router.locale === "ar-AE"
					? "حدد بعدًا واحدًا على الأقل"
					: "Select at least one option",
		other_plot_dimensions:
			router.locale === "kn-IN"
				? "ಇತರ ಪ್ಲಾಟ್ ಮುಟ್ಟಿಸಿ"
				: router.locale === "ar-AE"
					? "أبعاد قطعة الأرض الأخرى"
					: "Other plot dimensions",
		length: router.locale === "kn-IN" ? "ಉದ್ದ" : router.locale === "ar-AE" ? "الطول (قدم)" : "Length (ft)",
		width: router.locale === "kn-IN" ? "ಅಗಲ" : router.locale === "ar-AE" ? "العرض (قدم)" : "Width (ft)",
		addMore: router.locale === "kn-IN" ? "ಮತ್ತಷ್ಟು ತೋರಿಸಿ" : router.locale === "ar-AE" ? "أضف المزيد" : "Add more",
		sqft: router.locale === "kn-IN" ? "ಚದರ ಅಡಿ" : router.locale === "ar-AE" ? "قدم مربع" : "sqft",
		acre: router.locale === "kn-IN" ? "ಎಕರೆ" : router.locale === "ar-AE" ? "فدان" : "acre",
		sqm: router.locale === "kn-IN" ? "ಚದರ ಮೀಟರ್" : router.locale === "ar-AE" ? "متر مربع" : "sqm",
		land_area: router.locale === "kn-IN" ? "ಭೂಮಿಯ ವಿಸ್ತೀರ್ಣ" : router.locale === "ar-AE" ? "مساحة الأرض" : "Land area",
		unit: router.locale === "kn-IN" ? "ಯುನಿಟ್" : router.locale === "ar-AE" ? "وحدة القياس" : "Unit",
		area: router.locale === "kn-IN" ? "ವಿಸ್ತೀರ್ಣ" : router.locale === "ar-AE" ? "منطقة" : "Area",
		minimum_area:
			router.locale === "kn-IN"
				? "ಕನಿಷ್ಠ ವಿಸ್ತೀರ್ಣ"
				: router.locale === "ar-AE"
					? "الحد الأدنى للمنطقة"
					: "Minimum area",
		maximum_area:
			router.locale === "kn-IN"
				? "ಗರಿಷ್ಠ ವಿಸ್ತೀರ್ಣ"
				: router.locale === "ar-AE"
					? "الحد الأقصى للمنطقة"
					: "Maximum area",
	};

	const plot_dimensions = ["20 x 30", "30 x 40", "30 x 50", "40 x 60", "50 x 80", "60 x 90", "80 x 120", "Other"];

	const [isEditing, setIsEditing] = React.useState(false);
	const isSectionDirty = useEditSnapshot(isEditing);

	const [landAreaUnit, setLandAreaUnit] = React.useState<string>("acre");

	const [superBuiltUpAreaUnit, setSuperBuiltUpAreaUnit] = React.useState<string>("sqft");

	const [ca_is_percentage_of_sba, setCaIsPercentageOfSba] = React.useState<boolean>(false);

	const [minCarpetAreaPercentage, setMinCarpetAreaPercentage] = React.useState<number>(70);

	const [maxCarpetAreaPercentage, setMaxCarpetAreaPercentage] = React.useState<number>(70);

	const [carpetAreaUnit, setCarpetAreaUnit] = React.useState<string>("sqft");

	const newDelimitedSearchString = propertyData.search_metrics
		? propertyData.search_metrics.search_string_delimiter.split("ߐ")
		: [];

	const [submitDisabled, setSubmitDisabled] = React.useState<boolean>(false);

	const [errorBedroom, setErrorBedroom] = React.useState<boolean>(false);

	const [errorMinBedroom, setErrorMinBedroom] = React.useState<boolean>(false);

	const [errorMaxBedroom, setErrorMaxBedroom] = React.useState<boolean>(false);

	const [errorBathroom, setErrorBathroom] = React.useState<boolean>(false);

	const [errorMinBathroom, setErrorMinBathroom] = React.useState<boolean>(false);

	const [errorMaxBathroom, setErrorMaxBathroom] = React.useState<boolean>(false);

	const [errorNoOfSeats, setErrorNoOfSeats] = React.useState<boolean>(false);

	const [errorNoOfUnits, setErrorNoOfUnits] = React.useState<boolean>(false);

	const [errorMaxFloors, setErrorMaxFloors] = React.useState<boolean>(false);

	const [errorMinOtherPlot, setErrorMinOtherPlot] = React.useState<Array<boolean>>(
		new Array(property_plot_dimensions.filter((item) => !plot_dimensions.includes(item)).length).fill(false),
	);

	const [errorMaxOtherPlot, setErrorMaxOtherPlot] = React.useState<Array<boolean>>(
		new Array(property_plot_dimensions.filter((item) => !plot_dimensions.includes(item)).length).fill(false),
	);

	const [errorLandArea, setErrorLandArea] = React.useState<boolean>(false);

	const [errorSba, setErrorSba] = React.useState<boolean>(false);

	const [errorMinSba, setErrorMinSba] = React.useState<boolean>(false);

	const [errorMaxSba, setErrorMaxSba] = React.useState<boolean>(false);

	const [errorMinCa, setErrorMinCa] = React.useState<boolean>(false);

	const [errorMaxCa, setErrorMaxCa] = React.useState<boolean>(false);

	const [load, setLoad] = React.useState<boolean>(false);

	const [errorPlotDimensions, setErrorPlotDimensions] = React.useState<boolean>(false);

	const profile_context_user = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	React.useEffect(() => {
		if (section_to_edit !== "property-config") {
			setIsEditing(false);
		}
	}, [data_before_edit, section_to_edit]);

	if (loading) {
		return (
			<PaperContainer>
				<Contain>
					<Heading>
						<Skeleton
							animation="wave"
							variant="text"
							width="15rem"
						/>
					</Heading>
				</Contain>
				<TextContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="20rem"
					/>
				</TextContainer>
				<TextContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="20rem"
					/>
				</TextContainer>
				<TextContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="20rem"
					/>
				</TextContainer>
				<TextContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="20rem"
					/>
				</TextContainer>
			</PaperContainer>
		);
	}

	const configContainer = document.getElementById("config-area");

	/* Functions */

	const submitForm = async () => {
		/* Saving unlists the listing until it is approved again: say so first (gap A3, the app's prompt). */
		if (!(await confirmSendForReview())) return;
		setLoad(true);
		await dispatch(
			UpdateConfigAndAreaThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				property_id: property_id,
				search_string: newDelimitedSearchString.join(""),
				delimited_search_string: newDelimitedSearchString.join("ߐ"),
				property_type: propertyData.property_type,
				min_price: propertyData.price.min,
				max_price: propertyData.price.max,
				currency: propertyData.price?.currency === "AED" ? "AED" : "INR",
				land_area: property_area.land_area.max.acre,
				land_area_unit: landAreaUnit,
				min_super_built_up_area: property_area.super_built_up_area.min.ft,
				max_super_built_up_area: property_area.super_built_up_area.max.ft,
				super_built_up_area_unit: superBuiltUpAreaUnit,
				min_carpet_area: property_area.carpet_area.min.ft,
				max_carpet_area: property_area.carpet_area.max.ft,
				carpet_area_unit: carpetAreaUnit,
				plot_dimensions: property_plot_dimensions,
				other_plot_dimensions: other_plot_dimensions,
				furnishing: property_furnishing_status,
				min_bedrooms: property_bhk.min,
				max_bedrooms: property_bhk.max,
				min_bathrooms: property_bathroom.min,
				max_bathrooms: property_bathroom.max,
				min_balconies: property_balconies.min,
				max_balconies: property_balconies.max,
				total_floors: property_floors.max,
				floor_no: property_floor_no.max,
				no_of_seats: property_no_of_seats.max,
				no_of_units: property_no_of_units.max,
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: propertyData.created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				profile_context: profile_context,
			}),
		);
		dispatch(setSectionToEditThunk(""));
		dispatch(setDataBeforeEditThunk({}));
		setIsEditing(false);
		configContainer
			? configContainer.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "start",
				})
			: "";
		setLoad(false);
		setLandAreaUnit("acre");
		setSuperBuiltUpAreaUnit("sqft");
		setCarpetAreaUnit("sqft");
	};

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	const creator_user_id: string | ObjectId | UserInterface | BusinessProfileInterface = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: (property_created_by.business_profile_id as string)
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: (property_created_by.user_id as string)
		: "";

	const is_user_creator: boolean =
		profile_context_user !== ""
			? creator_user_id !== ""
				? profile_context_user === creator_user_id
					? true
					: false
				: false
			: false;

	return (
		<PaperContainer id={screenType + "_spp_config-area"}>
			{/* Icon */}

			<Snack
				open={config_area_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.snackValue}
				</Alert>
			</Snack>

			<Contain>
				<Heading>{localLang.configAndArea}</Heading>

				{is_user_creator ||
				(logged_user_access.includes("admin") &&
					logged_user_access.includes("property") &&
					propertyData.assigned_to.includes(logged_user)) ||
				logged_user_access.includes("super_admin") ||
				isEditor ? (
					<React.Fragment>
						{!isEditing ? (
							<IconButton
								id={screenType + "_spp_config_and_area_edit_button"}
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									dispatch(setSectionToEditThunk("property-config"));
									if (section_to_edit !== "property-config") {
										switch (section_to_edit) {
											case "property-details":
												dispatch(setSinglePropertyCategoryTypeThunk(data_before_edit.property_category_type));
												dispatch(setSinglePropertyTransactionTypeThunk(data_before_edit.property_transaction_type));
												dispatch(setSinglePropertyTypeThunk(data_before_edit.property_type));
												dispatch(setSinglePropertySubTypeThunk(data_before_edit.property_subtype));
												dispatch(setSinglePropertyMinPriceThunk(data_before_edit.property_min_price));
												dispatch(setSinglePropertyMaxPriceThunk(data_before_edit.property_max_price));
												dispatch(setSinglePropertySecurityDepositThunk(data_before_edit.property_security_deposit));
												dispatch(setSinglePropertyAvailabiltyThunk(data_before_edit.property_availability));
												dispatch(setSinglePropertyPossessionDateThunk(data_before_edit.property_possession_date));
												dispatch(setSinglePropertyTagsThunk(data_before_edit.property_tags));
												dispatch(setSinglePropertyUnitFacingThunk(data_before_edit.property_unit_facing));
												dispatch(setSinglePropertyLandFacingThunk(data_before_edit.property_land_facing));
												dispatch(setSinglePropertyAgeThunk(data_before_edit.property_age));
												break;
											case "property-overview":
												dispatch(setSinglePropertyOverviewThunk(data_before_edit));
												break;
											case "about-property":
												dispatch(setSinglePropertyAboutThunk(data_before_edit));
												break;
											case "property-amenities":
												dispatch(setSinglePropertyAmenitiesThunk(data_before_edit));
												break;
											case "property-developer":
												dispatch(setSinglePropertyDeveloperNameThunk(data_before_edit.name));
												dispatch(setSinglePropertyDescriptionThunk(data_before_edit.description));
												dispatch(setSinglePropertyEstablishedYearThunk(data_before_edit.year_est));
												break;
											case "property-location":
												dispatch(setSinglePropertyAboutLocationThunk(data_before_edit));
												break;
											case "property-vicinity":
												dispatch(setSinglePropertyVicinityThunk(data_before_edit));
												break;
											default:
												break;
										}
										dispatch(setSectionToEditThunk("property-config"));
									}
									setIsEditing(true);
									dispatch(
										setDataBeforeEditThunk({
											property_furnishing_status: property_furnishing_status,
											property_area: property_area.land_area.max.acre,
											land_area_unit: landAreaUnit,
											super_built_up_area_unit: superBuiltUpAreaUnit,
											carpet_area_unit: carpetAreaUnit,
											property_balconies_max: property_balconies.max,
											property_bathroom_max: property_bathroom.max,
											property_bhk_max: property_bhk.max,
											property_carpet_area_max: property_area.carpet_area.max.ft,
											property_floors_max: property_floors.max,
											super_builtup_max: property_area.super_built_up_area.max.ft,
											property_balconies_min: property_balconies.min,
											property_bathroom_min: property_bathroom.min,
											property_bedroom_min: property_bhk.min,
											property_carpet_area_mix: property_area.carpet_area.min.ft,
											super_builtup_min: property_area.super_built_up_area.min.ft,
											property_no_of_units: property_no_of_units.max,
											property_no_of_seats: property_no_of_seats.max,
											property_floor_no: property_floor_no.max,
											other_plot_dimensions: other_plot_dimensions,
											property_plot_dimensions: property_plot_dimensions,
										}),
									);
								}}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						) : (
							<IconButton
								id={screenType + "_spp_config_and_area_close_button"}
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={async () => {
									/* Unsaved changes ask first (3 Nov 2026, the app's "Discard changes?"). */
									if (isSectionDirty()) {
										const answer = await confirmDiscardChanges();
										if (answer === "keep") return;
										if (answer === "save") {
											skipNextSendForReviewConfirm();
											await submitForm();
											return;
										}
									}
									setIsEditing(false);
									dispatch(setSinglePropertyFurnishingThunk(data_before_edit.property_furnishing_status));
									dispatch(setSinglePropertyLandAreaThunk(data_before_edit.property_area));
									dispatch(setSinglePropertyMaxBalconiesThunk(data_before_edit.property_balconies_max));
									dispatch(setSinglePropertyMaxBathroomsThunk(data_before_edit.property_bathroom_max));
									dispatch(setSinglePropertyMaxBedroomsThunk(data_before_edit.property_bhk_max));
									dispatch(setSinglePropertyMaxCarpetAreaThunk(data_before_edit.property_carpet_area_max));
									dispatch(setSinglePropertyMaxFloorsThunk(data_before_edit.property_floors_max));
									dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(data_before_edit.super_builtup_max));
									dispatch(setSinglePropertyMinBalconiesThunk(data_before_edit.property_balconies_min));
									dispatch(setSinglePropertyMinBathroomsThunk(data_before_edit.property_bathroom_min));
									dispatch(setSinglePropertyMinBedroomsThunk(data_before_edit.property_bedroom_min));
									dispatch(setSinglePropertyMinCarpetAreaThunk(data_before_edit.property_carpet_area_mix));
									dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(data_before_edit.super_builtup_min));
									dispatch(setSinglePropertyNoOfUnitsThunk(data_before_edit.property_no_of_units));
									dispatch(setSinglePropertyNoOfSeatsThunk(data_before_edit.property_no_of_seats));
									dispatch(setSinglePropertyFloorNoThunk(data_before_edit.property_floor_no));
									dispatch(setSinglePropertyOtherPlotDimensionsThunk(data_before_edit.other_plot_dimensions));
									dispatch(setSinglePropertyPlotDimensionsThunk(data_before_edit.property_plot_dimensions));
									setLandAreaUnit("acre");
									setSuperBuiltUpAreaUnit("sqft");
									setCarpetAreaUnit("sqft");
									setErrorBathroom(false);
									setErrorBedroom(false);
									setErrorMaxBathroom(false);
									setErrorMaxBedroom(false);
									setErrorMinBathroom(false);
									setErrorMinBedroom(false);
									setErrorNoOfSeats(false);
									setErrorNoOfUnits(false);
									setErrorMaxFloors(false);
									setErrorMinOtherPlot(
										new Array(property_plot_dimensions.filter((item) => !plot_dimensions.includes(item)).length).fill(
											false,
										),
									);
									setErrorMaxOtherPlot(
										new Array(property_plot_dimensions.filter((item) => !plot_dimensions.includes(item)).length).fill(
											false,
										),
									);
									setErrorLandArea(false);
									setErrorSba(false);
								}}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						)}
					</React.Fragment>
				) : null}
			</Contain>

			{/* Furnishing */}

			{propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			!propertyData.property_type.includes("Plot") &&
			!propertyData.property_type.includes("Farm Plot") &&
			!propertyData.property_type.includes("Plot (Commercial)") &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Co-working") &&
			!propertyData.property_type.includes("Warehouse") &&
			!propertyData.property_type.includes("Factory") &&
			!propertyData.property_type.includes("Mining") ? (
				<TextContainer sx={{ padding: "0.5rem 0rem" }}>
					{!isEditing && (
						<ChairOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.furnishing + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{propertyData.furnished.length !== 0
									? propertyData.furnished
											.map((furnish: any) => furnish.charAt(0).toUpperCase() + furnish.slice(1).replace(/_/g, " "))
											.join(", ")
									: "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						<FormControl
							sx={{
								width: "100%",
								gap: "0.5rem",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<ChairOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.furnishing}
							</Typography>

							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									flexWrap: "wrap",
								}}
							>
								{[
									{ value: "furnished", label: localLang.furnished },
									{ value: "semi_furnished", label: localLang.semi_furnished },
									{ value: "unfurnished", label: localLang.unfurnished },
								].map((item, index) => {
									const isSelected = property_furnishing_status?.[0] === item.value;

									return (
										<CustomChip
											key={index}
											disableRipple
											disableTouchRipple
											disableFocusRipple
											isSelected={isSelected}
											onClick={() => {
												if (section_to_edit !== "property-config") {
													dispatch(setSectionToEditThunk("property-config"));
												}
												dispatch(setSinglePropertyFurnishingThunk([item.value]));
											}}
											variant="outlined"
										>
											<Text
												variant="body1"
												sx={{
													fontWeight: isSelected ? "500" : "400",
												}}
											>
												{item.value.replace("_", " ")}
											</Text>
										</CustomChip>
									);
								})}
							</Box>
						</FormControl>
					)}
				</TextContainer>
			) : null}

			{/* Bedrooms */}

			{/* {propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			!propertyData.property_type.includes("Plot") &&
			!propertyData.property_type.includes("Farm Plot") &&
			!propertyData.property_type.includes("Plot (Commercial)") &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Studio Apartment") &&
			!propertyData.property_type.includes("Building") &&
			!propertyData.property_type.includes("Shop") &&
			!propertyData.property_type.includes("Office Space") &&
			!propertyData.property_type.includes("Co-working") &&
			!propertyData.property_type.includes("Warehouse") &&
			!propertyData.property_type.includes("Factory") &&
			!propertyData.property_type.includes("Mining") ? (
				<TextContainer
					sx={{
						margin: "0rem 0rem 0rem 0rem",
					}}
				>
					<KingBedOutlined
						sx={{
							fontSize: "1.25rem",
							[theme.breakpoints.up("md1190")]: {
								fontSize: "1.5rem",
							},
							color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
						}}
					/>
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.bhk + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{propertyData.bhk.min === propertyData.bhk.max
									? propertyData.bhk.max > 0
										? formatBhkValue(propertyData.bhk.max)
										: ""
									: (propertyData.bhk.min > 0 ? formatBhkValue(propertyData.bhk.min) : "") +
										"-" +
										(propertyData.bhk.max > 0 ? formatBhkValue(propertyData.bhk.max) : "")}
							</CustomText>
						</DetailsContainer>
					) : propertyData.transaction_type !== "buy" ||
					  propertyData.property_type.includes("Co-living/PG") ||
					  propertyData.property_type.includes("Independent House") ? (
						<TextContainer
							sx={{
								margin: "0.5rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									margin: "0.5rem 0rem 0rem 0.5rem",
								},
							}}
						>
							<Typography
								sx={{
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
									mb: "10px",
								}}
							>
								{localLang.bedrooms}
							</Typography>

							<TextField
								id="spp_config_and_area_bedroom_edit_textfield"
								value={property_bhk.max === 0 ? "" : property_bhk.max}
								sx={{
									width: "100%",
									[theme.breakpoints.down("sm")]: {
										margin: "0rem 0rem 0rem 0rem",
										minWidth: "100%",
									},
								}}
								autoComplete="off"
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMinBedroomsThunk(Number(event.target.value.replace(/\D/g, ""))));
									dispatch(setSinglePropertyMaxBedroomsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (isNaN(Number(event.target.value.replace(/\D/g, "")))) {
										setErrorBedroom(true);
										setSubmitDisabled(true);
									} else {
										setErrorBedroom(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorBedroom}
								helperText={errorBedroom ? localLang.enter_a_valid_number : ""}
								// label={localLang.bedrooms + "*"}
							/>
						</TextContainer>
					) : (
						<TextContainer
							sx={{
								margin: "0.5rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									flexDirection: "column",
									margin: "0.5rem 0rem 0rem 0.5rem",
									gap: "1.5rem",
								},
							}}
						>
							<TextField
								id="spp_config_and_area_bedroom_min_edit_textfield"
								value={property_bhk.min === 0 ? "" : property_bhk.min}
								sx={{
									width: "50%",
									[theme.breakpoints.down("sm")]: {
										margin: "0rem 0rem 0rem 0rem",
										minWidth: "100%",
									},
								}}
								autoComplete="off"
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMinBedroomsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (
										(property_bhk.max !== 0 && Number(event.target.value.replace(/\D/g, "")) > property_bhk.max) ||
										isNaN(Number(event.target.value.replace(/\D/g, "")))
									) {
										setErrorMinBedroom(true);
										setErrorMaxBedroom(true);
										setSubmitDisabled(true);
									} else {
										setErrorMinBedroom(false);
										setErrorMaxBedroom(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorMinBedroom}
								helperText={errorMinBedroom ? localLang.minimum_value_is_more_than_maximum_value : ""}
								label={localLang.minimum_bedooms + "*"}
							/>

							<TextField
								id="spp_config_and_area_bedroom_max_edit_textfield"
								value={property_bhk.max === 0 ? "" : property_bhk.max}
								sx={{
									width: "50%",
									[theme.breakpoints.down("sm")]: {
										margin: "0rem 0rem 0rem 0rem",
										minWidth: "100%",
									},
								}}
								autoComplete="off"
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMaxBedroomsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (
										(property_bhk.min !== 0 && Number(event.target.value.replace(/\D/g, "")) < property_bhk.min) ||
										isNaN(Number(event.target.value.replace(/\D/g, "")))
									) {
										setErrorMinBedroom(true);
										setErrorMaxBedroom(true);
										setSubmitDisabled(true);
									} else {
										setErrorMinBedroom(false);
										setErrorMaxBedroom(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorMaxBedroom}
								helperText={errorMaxBedroom ? localLang.maximum_value_is_less_than_minimum_value : ""}
								label={localLang.maximum_bedrooms + "*"}
							/>
						</TextContainer>
					)}
				</TextContainer>
			) : null} */}

			{propertyData.transaction_type !== "ofb" &&
				propertyData.transaction_type !== "" &&
				propertyData.property_category !== "" &&
				propertyData.property_type.length !== 0 &&
				![
					"Plot",
					"Farm Plot",
					"Plot (Commercial)",
					"Land (Residential)",
					"Land (Commercial)",
					"Land (Agricultural)",
					"Land (Industrial)",
					"Studio Apartment",
					"Building (Commercial)",
					"Building (Residential)",
					"Shop",
					"Office Space",
					"Co-working",
					"Warehouse",
					"Factory",
					"Mining",
				].some((type) => propertyData.property_type.includes(type)) && (
					<TextContainer sx={{ padding: "0.5rem 0rem" }}>
						{!isEditing && (
							<KingBedOutlined
								sx={{
									fontSize: "1.25rem",
									[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
									color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
								}}
							/>
						)}
						{!isEditing ? (
							<DetailsContainer>
								<CustomText>{localLang.bhk + " :"}</CustomText>
								<CustomText
									sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
								>
									{propertyData.bhk.min === propertyData.bhk.max
										? propertyData.bhk.max > 0
											? formatBhkValue(propertyData.bhk.max)
											: "(not filled)"
										: (propertyData.bhk.min > 0 ? formatBhkValue(propertyData.bhk.min) + "-" : "") +
											(propertyData.bhk.max > 0 ? formatBhkValue(propertyData.bhk.max) : "(not filled)")}
								</CustomText>
							</DetailsContainer>
						) : propertyData.transaction_type !== "buy" ||
						  propertyData.property_type.includes("Co-living/PG") ||
						  propertyData.property_type.includes("Independent House") ? (
							<TextContainer
								sx={{
									flexDirection: "column",
									justifyContent: "flex-start",
									alignItems: "flex-start",
								}}
							>
								<Typography
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "0.5rem",
										fontFamily: "Poppins",
										fontSize: "14px",
										fontWeight: 500,
										lineHeight: "157%",
										letterSpacing: "0.1px",
									}}
								>
									<KingBedOutlined
										sx={{
											fontSize: "1.25rem",
											[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
											color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
										}}
									/>
									{localLang.bedrooms + "*"}
								</Typography>

								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "0.5rem",
										flexWrap: "wrap",
									}}
								>
									{["1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5", "6", "6+"].map((item) => {
										const val = item === "6+" ? 7 : parseFloat(item);
										const isSelected = property_bhk.max === val;

										return (
											<CustomChip
												key={item}
												disableRipple
												disableTouchRipple
												disableFocusRipple
												isSelected={isSelected}
												onClick={() => {
													if (section_to_edit !== "property-config") dispatch(setSectionToEditThunk("property-config"));
													dispatch(setSinglePropertyMinBedroomsThunk(val));
													dispatch(setSinglePropertyMaxBedroomsThunk(val));
													setErrorBedroom(false);
													setSubmitDisabled(false);
												}}
												variant="outlined"
											>
												<Text
													variant="body1"
													sx={{
														fontWeight: isSelected ? "500" : "400",
													}}
												>
													{item}
												</Text>
											</CustomChip>
										);
									})}
								</Box>

								{errorBedroom && <FormHelperText error>{localLang.enter_a_valid_number}</FormHelperText>}
							</TextContainer>
						) : (
							<TextContainer
								sx={{
									flexDirection: "column",
									gap: "0.5rem",
								}}
							>
								{/* Min BHK */}

								<FormControl sx={{ width: "100%", gap: "0.5rem" }}>
									<Typography
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											gap: "0.5rem",
											fontFamily: "Poppins",
											fontSize: "14px",
											fontWeight: 500,
											lineHeight: "157%",
											letterSpacing: "0.1px",
										}}
									>
										<KingBedOutlined
											sx={{
												fontSize: "1.25rem",
												[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
												color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
											}}
										/>
										{localLang.minimum_bedooms + "*"}
									</Typography>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											gap: "0.5rem",
											flexWrap: "wrap",
										}}
									>
										{["1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5", "6", "6+"].map((item) => {
											const val = item === "6+" ? 7 : parseFloat(item);
											const isSelected = property_bhk.min === val;

											return (
												<CustomChip
													key={item}
													disableRipple
													disableTouchRipple
													disableFocusRipple
													isSelected={isSelected}
													onClick={() => {
														if (section_to_edit !== "property-config")
															dispatch(setSectionToEditThunk("property-config"));
														dispatch(setSinglePropertyMinBedroomsThunk(val));
														if (property_bhk.max !== 0 && val > property_bhk.max) {
															setErrorMinBedroom(true);
															setErrorMaxBedroom(true);
															setSubmitDisabled(true);
														} else {
															setErrorMinBedroom(false);
															setErrorMaxBedroom(false);
															setSubmitDisabled(false);
														}
													}}
													variant="outlined"
												>
													<Text
														variant="body1"
														sx={{
															fontWeight: isSelected ? "500" : "400",
														}}
													>
														{item}
													</Text>
												</CustomChip>
											);
										})}
									</Box>
									{errorMinBedroom && (
										<FormHelperText error>{localLang.minimum_value_is_more_than_maximum_value}</FormHelperText>
									)}
								</FormControl>

								{/* Max BHK */}

								<FormControl sx={{ width: "100%", gap: "0.5rem" }}>
									<Typography
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											gap: "0.5rem",
											fontFamily: "Poppins",
											fontSize: "14px",
											fontWeight: 500,
											lineHeight: "157%",
											letterSpacing: "0.1px",
										}}
									>
										<KingBedOutlined
											sx={{
												fontSize: "1.25rem",
												[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
												color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
											}}
										/>
										{localLang.maximum_bedrooms + "*"}
									</Typography>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											gap: "0.5rem",
											flexWrap: "wrap",
										}}
									>
										{["1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5", "6", "6+"].map((item) => {
											const val = item === "6+" ? 7 : parseFloat(item);
											const isSelected = property_bhk.max === val;

											return (
												<CustomChip
													key={item}
													disableRipple
													disableTouchRipple
													disableFocusRipple
													isSelected={isSelected}
													onClick={() => {
														if (section_to_edit !== "property-config")
															dispatch(setSectionToEditThunk("property-config"));
														dispatch(setSinglePropertyMaxBedroomsThunk(val));
														if (property_bhk.min !== 0 && val < property_bhk.min) {
															setErrorMinBedroom(true);
															setErrorMaxBedroom(true);
															setSubmitDisabled(true);
														} else {
															setErrorMinBedroom(false);
															setErrorMaxBedroom(false);
															setSubmitDisabled(false);
														}
													}}
													variant="outlined"
												>
													<Text
														variant="body1"
														sx={{
															fontWeight: isSelected ? "500" : "400",
														}}
													>
														{item}
													</Text>
												</CustomChip>
											);
										})}
									</Box>
									{errorMaxBedroom && (
										<FormHelperText error>{localLang.maximum_value_is_less_than_minimum_value}</FormHelperText>
									)}
								</FormControl>
							</TextContainer>
						)}
					</TextContainer>
				)}

			{/* Bathrooms */}

			{/* {propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			!propertyData.property_type.includes("Co-living/PG") &&
			!propertyData.property_type.includes("Plot") &&
			!propertyData.property_type.includes("Farm Plot") &&
			!propertyData.property_type.includes("Plot (Commercial)") &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Studio Apartment") &&
			!propertyData.property_type.includes("Building") &&
			!propertyData.property_type.includes("Shop") &&
			!propertyData.property_type.includes("Office Space") &&
			!propertyData.property_type.includes("Co-working") &&
			!propertyData.property_type.includes("Warehouse") &&
			!propertyData.property_type.includes("Factory") &&
			!propertyData.property_type.includes("Mining") ? (
				<TextContainer
					sx={{
						margin: "0rem 0rem 0rem 0rem",
					}}
				>
					<BathtubOutlined
						sx={{
							fontSize: "1.25rem",
							[theme.breakpoints.up("md1190")]: {
								fontSize: "1.5rem",
							},
							color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
						}}
					/>
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.bathrooms + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{propertyData.bathroom.min === propertyData.bathroom.max
									? propertyData.bathroom.max > 0
										? propertyData.bathroom.max
										: "(not filled)"
									: (propertyData.bathroom.min > 0 ? propertyData.bathroom.min : "(not filled)") +
										"-" +
										(propertyData.bathroom.max > 0 ? propertyData.bathroom.max : "(not filled)")}
							</CustomText>
						</DetailsContainer>
					) : (propertyData.transaction_type !== "buy" && !propertyData.property_type.includes("Co-living/PG")) ||
					  propertyData.property_type.includes("Independent House") ? (
						<TextContainer
							sx={{
								margin: "0.5rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									margin: "0.5rem 0rem 0rem 0.5rem",
								},
							}}
						>
							<TextField
								id="spp_config_and_area_bathroom_max_edit_textfield"
								value={property_bathroom.max === 0 ? "" : property_bathroom.max}
								sx={{
									width: "100%",
									[theme.breakpoints.down("sm")]: {
										margin: "0rem 0rem 0rem 0rem",
										minWidth: "100%",
									},
								}}
								autoComplete="off"
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMinBathroomsThunk(Number(event.target.value.replace(/\D/g, ""))));
									dispatch(setSinglePropertyMaxBathroomsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (isNaN(Number(event.target.value.replace(/\D/g, "")))) {
										setErrorBathroom(true);
										setSubmitDisabled(true);
									} else {
										setErrorBathroom(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorBathroom}
								helperText={errorBathroom ? localLang.enter_a_valid_number : ""}
								label={localLang.bathrooms}
							/>
						</TextContainer>
					) : (
						<TextContainer
							sx={{
								margin: "0.5rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									flexDirection: "column",
									margin: "0.5rem 0rem 0rem 0.5rem",
									gap: "1.5rem",
								},
							}}
						>
							<TextField
								id="spp_config_and_area_bathroom_min_edit_textfield"
								value={property_bathroom.min === 0 ? "" : property_bathroom.min}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								autoComplete="off"
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMinBathroomsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (
										(property_bathroom.max !== 0 &&
											Number(event.target.value.replace(/\D/g, "")) > property_bathroom.max) ||
										isNaN(Number(event.target.value.replace(/\D/g, "")))
									) {
										setErrorMinBathroom(true);
										setErrorMaxBathroom(true);
										setSubmitDisabled(true);
									} else {
										setErrorMinBathroom(false);
										setErrorMaxBathroom(false);
										setSubmitDisabled(false);
									}
								}}
								sx={{
									width: "50%",
									[theme.breakpoints.down("sm")]: {
										margin: "0rem 0rem 0rem 0rem",
										minWidth: "100%",
									},
								}}
								error={errorMinBathroom}
								helperText={errorMinBathroom ? localLang.minimum_value_is_more_than_maximum_value : ""}
								label={localLang.minimum_bathrooms}
							/>

							<TextField
								id="spp_config_and_area_bathroom_max_edit_textfield"
								value={property_bathroom.max === 0 ? "" : property_bathroom.max}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								autoComplete="off"
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMaxBathroomsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (
										(property_bathroom.min !== 0 &&
											Number(event.target.value.replace(/\D/g, "")) < property_bathroom.min) ||
										isNaN(Number(event.target.value.replace(/\D/g, "")))
									) {
										setErrorMinBathroom(true);
										setErrorMaxBathroom(true);
										setSubmitDisabled(true);
									} else {
										setErrorMinBathroom(false);
										setErrorMaxBathroom(false);
										setSubmitDisabled(false);
									}
								}}
								sx={{
									width: "50%",
									[theme.breakpoints.down("sm")]: {
										margin: "0rem 0rem 0rem 0rem",
										minWidth: "100%",
									},
								}}
								error={errorMaxBathroom}
								helperText={errorMaxBathroom ? localLang.maximum_value_is_less_than_minimum_value : ""}
								label={localLang.maximum_bathrooms}
							/>
						</TextContainer>
					)}
				</TextContainer>
			) : null} */}

			{propertyData.transaction_type !== "ofb" &&
				propertyData.transaction_type !== "" &&
				propertyData.property_category !== "" &&
				propertyData.property_type.length !== 0 &&
				![
					"Plot",
					"Farm Plot",
					"Plot (Commercial)",
					"Land (Residential)",
					"Land (Commercial)",
					"Land (Agricultural)",
					"Land (Industrial)",
					"Studio Apartment",
					"Building (Commercial)",
					"Building (Residential)",
					"Shop",
					"Office Space",
					"Co-working",
					"Warehouse",
					"Factory",
					"Mining",
					"Co-living/PG",
				].some((type) => propertyData.property_type.includes(type)) && (
					<TextContainer sx={{ padding: "0.5rem 0rem" }}>
						{!isEditing && (
							<BathtubOutlined
								sx={{
									fontSize: "1.25rem",
									[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
									color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
								}}
							/>
						)}
						{!isEditing ? (
							<DetailsContainer>
								<CustomText>{localLang.bathrooms + " :"}</CustomText>
								<CustomText
									sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
								>
									{propertyData.bathroom.min === propertyData.bathroom.max
										? propertyData.bathroom.max > 0
											? propertyData.bathroom.max === 5
												? "5+"
												: propertyData.bathroom.max
											: "(not filled)"
										: (propertyData.bathroom.min > 0 ? propertyData.bathroom.min : "") +
											"-" +
											(propertyData.bathroom.max > 0
												? propertyData.bathroom.max === 5
													? "5+"
													: propertyData.bathroom.max
												: "(not filled)")}
								</CustomText>
							</DetailsContainer>
						) : propertyData.transaction_type !== "buy" || propertyData.property_type.includes("Independent House") ? (
							<TextContainer
								sx={{
									flexDirection: "column",
									justifyContent: "flex-start",
									alignItems: "flex-start",
								}}
							>
								<Typography
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "0.5rem",
										fontFamily: "Poppins",
										fontSize: "14px",
										fontWeight: 500,
										lineHeight: "157%",
										letterSpacing: "0.1px",
									}}
								>
									<BathtubOutlined
										sx={{
											fontSize: "1.25rem",
											[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
											color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
										}}
									/>
									{localLang.bathrooms + "*"}
								</Typography>

								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "0.5rem",
										flexWrap: "wrap",
									}}
								>
									{["1", "2", "3", "4", "5+"].map((item) => {
										const val = item === "5+" ? 5 : parseInt(item);
										const isSelected = property_bathroom.max === val;

										return (
											<CustomChip
												key={item}
												disableRipple
												disableTouchRipple
												disableFocusRipple
												isSelected={isSelected}
												onClick={() => {
													if (section_to_edit !== "property-config") dispatch(setSectionToEditThunk("property-config"));
													dispatch(setSinglePropertyMinBathroomsThunk(val));
													dispatch(setSinglePropertyMaxBathroomsThunk(val));
													setErrorBathroom(false);
													setSubmitDisabled(false);
												}}
												variant="outlined"
											>
												<Text
													variant="body1"
													sx={{
														fontWeight: isSelected ? "500" : "400",
													}}
												>
													{item}
												</Text>
											</CustomChip>
										);
									})}
								</Box>

								{errorBathroom && <FormHelperText error>{localLang.enter_a_valid_number}</FormHelperText>}
							</TextContainer>
						) : (
							<TextContainer
								sx={{
									flexDirection: "column",
									gap: "0.5rem",
								}}
							>
								{/* Min Bathrooms */}

								<FormControl sx={{ width: "100%", gap: "0.5rem" }}>
									<Typography
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											gap: "0.5rem",
											fontFamily: "Poppins",
											fontSize: "14px",
											fontWeight: 500,
											lineHeight: "157%",
											letterSpacing: "0.1px",
										}}
									>
										<BathtubOutlined
											sx={{
												fontSize: "1.25rem",
												[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
												color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
											}}
										/>
										{localLang.minimum_bathrooms + "*"}
									</Typography>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											gap: "0.5rem",
											flexWrap: "wrap",
										}}
									>
										{["1", "2", "3", "4", "5+"].map((item) => {
											const val = item === "5+" ? 5 : parseInt(item);
											const isSelected = property_bathroom.min === val;

											return (
												<CustomChip
													key={item}
													disableRipple
													disableTouchRipple
													disableFocusRipple
													isSelected={isSelected}
													onClick={() => {
														if (section_to_edit !== "property-config")
															dispatch(setSectionToEditThunk("property-config"));
														dispatch(setSinglePropertyMinBathroomsThunk(val));
														if (property_bathroom.max !== 0 && val > property_bathroom.max) {
															setErrorMinBathroom(true);
															setErrorMaxBathroom(true);
															setSubmitDisabled(true);
														} else {
															setErrorMinBathroom(false);
															setErrorMaxBathroom(false);
															setSubmitDisabled(false);
														}
													}}
													variant="outlined"
												>
													<Text
														variant="body1"
														sx={{
															fontWeight: isSelected ? "500" : "400",
														}}
													>
														{item}
													</Text>
												</CustomChip>
											);
										})}
									</Box>
									{errorMinBathroom && (
										<FormHelperText error>{localLang.minimum_value_is_more_than_maximum_value}</FormHelperText>
									)}
								</FormControl>

								{/* Max Bathrooms */}

								<FormControl sx={{ width: "100%", gap: "0.5rem" }}>
									<Typography
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											gap: "0.5rem",
											fontFamily: "Poppins",
											fontSize: "14px",
											fontWeight: 500,
											lineHeight: "157%",
											letterSpacing: "0.1px",
										}}
									>
										<BathtubOutlined
											sx={{
												fontSize: "1.25rem",
												[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
												color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
											}}
										/>
										{localLang.maximum_bathrooms + "*"}
									</Typography>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "flex-start",
											alignItems: "center",
											gap: "0.5rem",
											flexWrap: "wrap",
										}}
									>
										{["1", "2", "3", "4", "5+"].map((item) => {
											const val = item === "5+" ? 5 : parseInt(item);
											const isSelected = property_bathroom.max === val;

											return (
												<CustomChip
													key={item}
													disableRipple
													disableTouchRipple
													disableFocusRipple
													isSelected={isSelected}
													onClick={() => {
														if (section_to_edit !== "property-config")
															dispatch(setSectionToEditThunk("property-config"));
														dispatch(setSinglePropertyMaxBathroomsThunk(val));
														if (property_bathroom.min !== 0 && val < property_bathroom.min) {
															setErrorMinBathroom(true);
															setErrorMaxBathroom(true);
															setSubmitDisabled(true);
														} else {
															setErrorMinBathroom(false);
															setErrorMaxBathroom(false);
															setSubmitDisabled(false);
														}
													}}
													variant="outlined"
												>
													<Text
														variant="body1"
														sx={{
															fontWeight: isSelected ? "500" : "400",
														}}
													>
														{item}
													</Text>
												</CustomChip>
											);
										})}
									</Box>
									{errorMaxBathroom && (
										<FormHelperText error>{localLang.maximum_value_is_less_than_minimum_value}</FormHelperText>
									)}
								</FormControl>
							</TextContainer>
						)}
					</TextContainer>
				)}

			{/* Balconies */}

			{/* {propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			!propertyData.property_type.includes("Co-living/PG") &&
			!propertyData.property_type.includes("Plot") &&
			!propertyData.property_type.includes("Farm Plot") &&
			!propertyData.property_type.includes("Plot (Commercial)") &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Building") &&
			!propertyData.property_type.includes("Shop") &&
			!propertyData.property_type.includes("Office Space") &&
			!propertyData.property_type.includes("Co-working") &&
			!propertyData.property_type.includes("Warehouse") &&
			!propertyData.property_type.includes("Factory") &&
			!propertyData.property_type.includes("Mining") ? (
				<TextContainer>
					<BalconyOutlined
						sx={{
							fontSize: "1.25rem",
							[theme.breakpoints.up("md1190")]: {
								fontSize: "1.5rem",
							},
							color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
						}}
					/>
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.balconies + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{propertyData.balconies.min === propertyData.balconies.max
									? propertyData.balconies.max > 0
										? propertyData.balconies.max
										: "(not filled)"
									: (propertyData.balconies.min > 0 ? propertyData.balconies.min : "(not filled)") +
										"-" +
										(propertyData.balconies.max > 0 ? propertyData.balconies.max : "(not filled)")}
							</CustomText>
						</DetailsContainer>
					) : (propertyData.transaction_type !== "buy" && !propertyData.property_type.includes("Co-living/PG")) ||
					  propertyData.property_type.includes("Independent House") ? (
						<TextContainer
							sx={{
								margin: "0.5rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									margin: "0.5rem 0rem 0rem 0.5rem",
								},
							}}
						>
							<TextField
								id="spp_config_and_area_balcony_edit_textfield"
								value={property_balconies.max === 0 ? "" : property_balconies.max}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								sx={{
									width: "100%",
								}}
								autoComplete="off"
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMinBalconiesThunk(Number(event.target.value.replace(/\D/g, ""))));
									dispatch(setSinglePropertyMaxBalconiesThunk(Number(event.target.value.replace(/\D/g, ""))));
								}}
								label={localLang.balconies}
							/>
						</TextContainer>
					) : (
						<TextContainer
							sx={{
								margin: "0.5rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									margin: "0.5rem 0rem 0rem 0.5rem",
								},
							}}
						>
							<TextField
								id="spp_config_and_area_balcony_edit_textfield"
								value={property_balconies.max === 0 ? "" : property_balconies.max}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								sx={{
									width: "100%",
								}}
								autoComplete="off"
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMinBalconiesThunk(Number(event.target.value.replace(/\D/g, ""))));
									dispatch(setSinglePropertyMaxBalconiesThunk(Number(event.target.value.replace(/\D/g, ""))));
								}}
								label={localLang.balconies}
							/>
						</TextContainer>
						// <DetailsContainer>
						//   <TextField
						//     value={property_balconies.min}
						//     onChange={(event: any) => dispatch(setSinglePropertyMinBalconiesThunk(event.target.value))}
						//     label="Minimum Balconies"
						//   />
						//   <TextField
						//     value={property_balconies.max}
						//     onChange={(event: any) => dispatch(setSinglePropertyMaxBalconiesThunk(event.target.value))}
						//     label="Maximum Balconies"

						//   />
						// </DetailsContainer>
					)}
				</TextContainer>
			) : null} */}

			{propertyData.transaction_type !== "ofb" &&
				propertyData.transaction_type !== "" &&
				propertyData.property_category !== "" &&
				propertyData.property_type.length !== 0 &&
				![
					"Plot",
					"Farm Plot",
					"Plot (Commercial)",
					"Land (Residential)",
					"Land (Commercial)",
					"Land (Agricultural)",
					"Land (Industrial)",
					"Building (Commercial)",
					"Building (Residential)",
					"Shop",
					"Office Space",
					"Co-working",
					"Warehouse",
					"Factory",
					"Mining",
					"Co-living/PG",
				].some((type) => propertyData.property_type.includes(type)) && (
					<TextContainer sx={{ padding: "0.5rem 0rem" }}>
						{!isEditing && (
							<BalconyOutlined
								sx={{
									fontSize: "1.25rem",
									[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
									color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
								}}
							/>
						)}
						{!isEditing ? (
							<DetailsContainer>
								<CustomText>{localLang.balconies + " :"}</CustomText>
								<CustomText
									sx={{
										fontWeight: 500,
										fontSize: "1rem",
										[theme.breakpoints.down("sm")]: { fontSize: "0.875rem" },
									}}
								>
									{propertyData.balconies.max > 0
										? propertyData.balconies.max === 5
											? "5+"
											: propertyData.balconies.max
										: "(not filled)"}
								</CustomText>
							</DetailsContainer>
						) : (
							<FormControl sx={{ width: "100%", gap: "0.5rem" }}>
								<Typography
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "0.5rem",
										fontFamily: "Poppins",
										fontSize: "14px",
										fontWeight: 500,
										lineHeight: "157%",
										letterSpacing: "0.1px",
									}}
								>
									<BalconyOutlined
										sx={{
											fontSize: "1.25rem",
											[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
											color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
										}}
									/>
									{localLang.balconies + "*"}
								</Typography>
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "0.5rem",
										flexWrap: "wrap",
									}}
								>
									{["1", "2", "3", "4", "5+"].map((item) => {
										const val = item === "5+" ? 5 : parseInt(item);
										const isSelected = property_balconies.max === val;

										return (
											<CustomChip
												key={item}
												disableRipple
												disableTouchRipple
												disableFocusRipple
												isSelected={isSelected}
												onClick={() => {
													if (section_to_edit !== "property-config") dispatch(setSectionToEditThunk("property-config"));
													if (isSelected) {
														// Uncheck the balcony if it is already selected
														dispatch(setSinglePropertyMinBalconiesThunk(0));
														dispatch(setSinglePropertyMaxBalconiesThunk(0));
													} else {
														// Select the balcony
														dispatch(setSinglePropertyMinBalconiesThunk(val));
														dispatch(setSinglePropertyMaxBalconiesThunk(val));
													}
												}}
												variant="outlined"
											>
												<Text
													variant="body1"
													sx={{
														fontWeight: isSelected ? "500" : "400",
													}}
												>
													{item}
												</Text>
											</CustomChip>
										);
									})}
								</Box>
							</FormControl>
						)}
					</TextContainer>
				)}

			{/* No of Seats */}

			{propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			propertyData.property_type.includes("Co-working") ? (
				<TextContainer sx={{ padding: "0.5rem 0rem" }}>
					{!isEditing && (
						<EventSeatOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.noOfSeats + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_no_of_seats.max > 0 ? property_no_of_seats.max : "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						<TextContainer
							sx={{
								// 	margin: "0.5rem 0rem 0rem 0rem",
								// 	[theme.breakpoints.down("sm")]: {
								// 		margin: "0.5rem 0rem 0rem 0.5rem",
								// 	},
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<EventSeatOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.noOfSeats + "*"}
							</Typography>
							<TextField
								id={screenType + "_spp_config_and_area_no_of_seats_edit_textfield"}
								value={property_no_of_seats.max === 0 ? "" : property_no_of_seats.max}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								sx={{
									width: "100%",
								}}
								autoComplete="off"
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyNoOfSeatsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (isNaN(Number(event.target.value.replace(/\D/g, "")))) {
										setErrorNoOfSeats(true);
										setSubmitDisabled(true);
									} else {
										setErrorNoOfSeats(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorNoOfSeats}
								helperText={errorNoOfSeats ? localLang.enter_a_valid_number : ""}
								// label={localLang.noOfSeats + "*"}
								placeholder={localLang.noOfSeats + "*"}
							/>
						</TextContainer>
					)}
				</TextContainer>
			) : null}

			{/* {propertyData.transaction_type !== "ofb" &&
				propertyData.transaction_type !== "" &&
				propertyData.property_category !== "" &&
				propertyData.property_type.length !== 0 &&
				propertyData.property_type.includes("Co-working") && (
					<TextContainer sx={{ padding: "0.5rem 0rem" }}>
						<EventSeatOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
								color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
							}}
						/>

						{!isEditing ? (
							<DetailsContainer>
								<CustomText>{localLang.noOfSeats + " :"}</CustomText>
								<CustomText
									sx={{
										fontWeight: 500,
										fontSize: "1rem",
										[theme.breakpoints.down("sm")]: { fontSize: "0.875rem" },
									}}
								>
									{property_no_of_seats.max > 0 ? property_no_of_seats.max : "(not filled)"}
								</CustomText>
							</DetailsContainer>
						) : (
							<FormControl sx={{ width: "100%", gap: "0.5rem" }}>
								<Typography
									sx={{
										fontFamily: "Poppins",
										fontSize: "14px",
										fontWeight: 500,
										lineHeight: "157%",
										letterSpacing: "0.1px",
									}}
								>
									{localLang.noOfSeats + "*"}
								</Typography>

								<Box sx={{
							display: "flex",
							flexDirection: "row",
							justifyContent: "flex-start",
							alignItems: "center",
							gap: "0.5rem",
							flexWrap: "wrap",
						}}>
									{["5", "10", "15", "20", "25", "30", "35", "40+"].map((label) => {
										const value = label === "40+" ? 40 : parseInt(label);
										const isSelected = property_no_of_seats.max === value;

										return (
											<Chip
												key={label}
												label={label}
												clickable
												onClick={() => {
													if (section_to_edit !== "property-config") {
														dispatch(setSectionToEditThunk("property-config"));
													}
													dispatch(setSinglePropertyNoOfSeatsThunk(value));
													setErrorNoOfSeats(false);
													setSubmitDisabled(false);
												}}
												sx={{
													borderRadius: "8px",
													border: isSelected
														? "1px solid var(--Primary-New-Dark, #B05911)"
														: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
													backgroundColor: isSelected ? "#FFEBDA" : "#fff",
													color: isSelected ? "#B05911" : "inherit",
													fontWeight: 400,
													fontSize: "14px",
													height: "40px",
													padding: "0.75rem",
												}}
											/>
										);
									})}
								</Box>
							</FormControl>
						)}
					</TextContainer>
				)} */}

			{/* No of Units */}

			{propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			((propertyData.transaction_type === "buy" &&
				(propertyData.property_type.includes("Apartment") ||
					propertyData.property_type.includes("Plot") ||
					propertyData.property_type.includes("Farm Plot") ||
					propertyData.property_type.includes("Plot (Commercial)") ||
					propertyData.property_type.includes("Villa") ||
					propertyData.property_type.includes("Row House") ||
					propertyData.property_type.includes("Builder Floor Apartment") ||
					propertyData.property_type.includes("Building (Residential)") ||
					propertyData.property_type.includes("Studio Apartment") ||
					propertyData.property_type.includes("Penthouse"))) ||
				(propertyData.transaction_type === "rent" && propertyData.property_type.includes("Building (Residential)")) ||
				(propertyData.transaction_type === "resale" &&
					propertyData.property_type.includes("Building (Residential)"))) ? (
				<TextContainer sx={{ padding: "0.5rem 0rem" }}>
					{!isEditing && (
						<CorporateFareRounded
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.totalNoOfUnits + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_no_of_units.max > 0 ? property_no_of_units.max : "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						<TextContainer
							sx={{
								// 	margin: "0.5rem 0rem 0rem 0rem",
								// 	[theme.breakpoints.down("sm")]: {
								// 		margin: "0.5rem 0rem 0rem 0.5rem",
								// 	},
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<CorporateFareRounded
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.totalNoOfUnits + "*"}
							</Typography>
							<TextField
								id={screenType + "_spp_config_and_area_no_of_units_edit_textfield"}
								value={property_no_of_units.max === 0 ? "" : property_no_of_units.max}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								autoComplete="off"
								sx={{
									width: "100%",
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyNoOfUnitsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (isNaN(Number(event.target.value.replace(/\D/g, "")))) {
										setErrorNoOfUnits(true);
										setSubmitDisabled(true);
									} else {
										setErrorNoOfUnits(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorNoOfUnits}
								helperText={errorNoOfUnits ? localLang.enter_a_valid_number : ""}
								placeholder={localLang.totalNoOfUnits + "*"}
							/>
						</TextContainer>
					)}
				</TextContainer>
			) : null}

			{/* {propertyData.transaction_type !== "" &&
				propertyData.property_category !== "" &&
				propertyData.property_type.length !== 0 &&
				propertyData.transaction_type === "buy" &&
				[
					"Apartment",
					"Plot",
					"Farm Plot",
					"Plot (Commercial)",
					"Villa",
					"Row House",
					"Builder Floor Apartment",
					"Studio Apartment",
					"Penthouse",
				].some((type) => propertyData.property_type.includes(type)) && (
					<TextContainer sx={{ padding: "0.5rem 0rem" }}>
						<CorporateFareRounded
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
								color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
							}}
						/>
						{!isEditing ? (
							<DetailsContainer>
								<CustomText>{localLang.totalNoOfUnits + " :"}</CustomText>
								<CustomText
									sx={{
										fontWeight: 500,
										fontSize: "1rem",
										[theme.breakpoints.down("sm")]: { fontSize: "0.875rem" },
									}}
								>
									{property_no_of_units.max > 0 ? property_no_of_units.max : "(not filled)"}
								</CustomText>
							</DetailsContainer>
						) : (
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 1,
									marginTop: "0.5rem",
									marginLeft: { xs: "0.5rem", sm: 0 },
								}}
							>
								{Array.from({ length: 100 }, (_, i) => i + 1).map((unit) => (
									<Chip
										key={unit}
										label={unit}
										color={property_no_of_units.max === unit ? "primary" : "default"}
										variant={property_no_of_units.max === unit ? "filled" : "outlined"}
										onClick={() => {
											if (section_to_edit !== "property-config") {
												dispatch(setSectionToEditThunk("property-config"));
											}
											dispatch(setSinglePropertyNoOfUnitsThunk(unit));
											setErrorNoOfUnits(false);
											setSubmitDisabled(false);
										}}
										sx={{ cursor: "pointer" }}
									/>
								))}
							</Box>
						)}
					</TextContainer>
				)} */}

			{/* Floor No */}

			{/* {transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			transaction_type !== "ofb" &&
			!propertyData.property_type.includes("Plot") &&
			!propertyData.property_type.includes("Farm Plot") &&
			!propertyData.property_type.includes("Plot (Commercial)") &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Warehouse") &&
			!propertyData.property_type.includes("Factory") &&
			!propertyData.property_type.includes("Mining") &&
			!propertyData.property_type.includes("Villa") &&
			!propertyData.property_type.includes("Row House") ? (
				(transaction_type == "rent" && !propertyData.property_type.includes("Independent House")) ||
				(transaction_type == "rent" && !propertyData.property_type.includes("Co-living/PG")) ? (
					<TextContainer>
						<DomainAdd
							sx={{
							fontSize: "1.25rem",
							[theme.breakpoints.up("md1190")]: {
								fontSize: "1.5rem",
							},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
						{!isEditing ? (
							<CustomText>{"Floor No: " + property_floor_no.max}</CustomText>
						) : (
							<DetailsContainer>
								<TextField
									value={property_floor_no.max}
									onChange={(event: any) => {
										dispatch(setSinglePropertyFloorNoThunk(event.target.value));
									}}
									label="Floor"
									inputProps={{
									min: 0,
									inputMode: "numeric",
									pattern: "[0-9]*",
								}}
								/>
							</DetailsContainer>
						)}
					</TextContainer>
				) : null
			) : null} */}

			{/* No of Floors */}

			{propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			!propertyData.property_type.includes("Plot") &&
			!propertyData.property_type.includes("Farm Plot") &&
			!propertyData.property_type.includes("Plot (Commercial)") &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Warehouse") &&
			!propertyData.property_type.includes("Factory") &&
			!propertyData.property_type.includes("Mining") ? (
				<TextContainer sx={{ padding: "0.5rem 0rem" }}>
					{!isEditing && (
						<StairsOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.totalNoOfFloors + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_floors.max > 0 ? property_floors.max : "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						//   <TextField
						//   value={minFloors}
						//   onChange={(event: any) => setMinFloors(event.target.value)}
						//   size="small"
						//   label="Minimum Floors"
						//   margin="dense"
						//   // sx={{ width: "5rem" }}
						// />
						// <TextField
						//   value={property_floors.max}
						//   onChange={(event: any) => dispatch(setSinglePropertyMaxFloorsThunk(event.target.value))}
						//   size="small"
						//   label="Maximum Floors"
						//   margin="dense"
						//   />   (transaction_type == "resale" &&
						//         !propertyData.property_type.includes("Independent House") &&
						//         !propertyData.property_type.includes("Villa") &&
						//         !propertyData.property_type.includes("Co-living/PG") &&
						//         !propertyData.property_type.includes("Row House") &&
						//         !propertyData.property_type.includes("Building") &&
						//         !propertyData.property_type.includes("Shop") &&
						//         !propertyData.property_type.includes("Office Space") &&
						//         !propertyData.property_type.includes("Co-working") &&
						//         !propertyData.property_type.includes("Farm House")) ||
						//       (transaction_type == "rent" &&
						//         !propertyData.property_type.includes("Independent House") &&
						//         !propertyData.property_type.includes("Villa") &&
						//         !propertyData.property_type.includes("Row House") &&
						//         !propertyData.property_type.includes("Farm House"))
						//         ? "Floor"
						//     : No. of floors
						<TextContainer
							sx={{
								// 	margin: "0.5rem 0rem 0rem 0rem",
								// 	[theme.breakpoints.down("sm")]: {
								// 		margin: "0.5rem 0rem 0rem 0.5rem",
								// 	},
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<StairsOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.totalNoOfFloors}
							</Typography>
							<TextField
								id={screenType + "_spp_config_and_area_no_of_floors_edit_textfield"}
								value={property_floors.max === 0 ? "" : property_floors.max}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								sx={{
									width: "100%",
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									// setMinFloors(event.target.value);
									dispatch(setSinglePropertyFloorNoThunk(Number(event.target.value.replace(/\D/g, ""))));
									dispatch(setSinglePropertyMaxFloorsThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (isNaN(Number(event.target.value.replace(/\D/g, "")))) {
										setErrorMaxFloors(true);
										setSubmitDisabled(true);
									} else {
										setErrorMaxFloors(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorMaxFloors}
								helperText={errorMaxFloors ? localLang.enter_a_valid_number : ""}
								placeholder={localLang.totalNoOfFloors}
								autoComplete="off"
							/>
						</TextContainer>
					)}
				</TextContainer>
			) : null}

			{/* {propertyData.transaction_type !== "ofb" &&
				propertyData.transaction_type !== "" &&
				propertyData.property_category !== "" &&
				propertyData.property_type.length !== 0 &&
				![
					"Plot",
					"Farm Plot",
					"Plot (Commercial)",
					"Land (Residential)",
					"Land (Commercial)",
					"Land (Agricultural)",
					"Land (Industrial)",
					"Warehouse",
					"Factory",
					"Mining",
				].some((type) => propertyData.property_type.includes(type)) && (
					<TextContainer sx={{ padding: "0.5rem 0" }}>
						<StairsOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
								color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
							}}
						/>

						{!isEditing ? (
							<DetailsContainer>
								<CustomText>{localLang.totalNoOfFloors + " :"}</CustomText>
								<CustomText
									sx={{
										fontWeight: 500,
										fontSize: "1rem",
										[theme.breakpoints.down("sm")]: { fontSize: "0.875rem" },
									}}
								>
									{property_floors.max > 0 ? property_floors.max : "(not filled)"}
								</CustomText>
							</DetailsContainer>
						) : (
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 1,
									marginTop: "0.5rem",
									marginLeft: { xs: "0.5rem", sm: 0 },
								}}
							>
								{Array.from({ length: 50 }, (_, i) => i + 1).map((floor) => (
									<Chip
										key={floor}
										label={floor}
										color={property_floors.max === floor ? "primary" : "default"}
										variant={property_floors.max === floor ? "filled" : "outlined"}
										onClick={() => {
											if (section_to_edit !== "property-config") {
												dispatch(setSectionToEditThunk("property-config"));
											}
											dispatch(setSinglePropertyFloorNoThunk(floor));
											dispatch(setSinglePropertyMaxFloorsThunk(floor));
											setErrorMaxFloors(false);
											setSubmitDisabled(false);
										}}
										sx={{ cursor: "pointer" }}
									/>
								))}
							</Box>
						)}
					</TextContainer>
				)} */}

			{/* Plot Dimensions */}

			{propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			(propertyData.property_type.includes("Plot") ||
				propertyData.property_type.includes("Farm Plot") ||
				propertyData.property_type.includes("Plot (Commercial)")) ? (
				<TextContainer sx={{ padding: "0.5rem 0rem" }}>
					{!isEditing && (
						<AspectRatioOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.PlotDimensions + " :"}</CustomText>
							<CustomText
								sx={{
									overflowX: "scroll",
									overflowY: "hidden",
									"&::-webkit-scrollbar": {
										display: "none",
									},
									flexWrap: "wrap",
									fontWeight: 500,
									fontSize: "1rem",
									[theme.breakpoints.down("sm")]: { fontSize: "0.875rem" },
								}}
							>
								{propertyData.other_plot_dimensions.length !== 0
									? [...propertyData.plot_dimensions, ...propertyData.other_plot_dimensions]
											.filter((dimension: any) => dimension !== "Other")
											.map((dimension: any) => dimension)
											.join(", ")
									: propertyData.plot_dimensions
											.filter((dimension: any) => dimension !== "Other")
											.map((dimension: any) => dimension)
											.join(", ")}
							</CustomText>
						</DetailsContainer>
					) : (
						// <FormControl
						// 	sx={{
						// 		width: "100%",
						// 		margin: "0rem 0rem 0rem 0rem",
						// 		[theme.breakpoints.down("sm")]: {
						// 			margin: "0.5rem 0rem 0rem 0.5rem",
						// 		},
						// 	}}
						// 	error={errorPlotDimensions}
						// >
						// 	<Typography
						// 		sx={{
						// 			display: "flex",
						// 			flexDirection: "row",
						// 			justifyContent: "flex-start",
						// 			alignItems: "center",
						// 			gap: "0.5rem",
						// 			fontFamily: "Poppins",
						// 			fontSize: "14px",
						// 			fontWeight: 500,
						// 			lineHeight: "157%",
						// 			letterSpacing: "0.1px",
						// 		}}
						// 	>
						// 		<AspectRatioOutlined
						// 			sx={{
						// 				fontSize: "1.25rem",
						// 				[theme.breakpoints.up("md1190")]: {
						// 					fontSize: "1.5rem",
						// 				},
						// 				color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
						// 			}}
						// 		/>
						// 		{localLang.PlotDimensions + "*"}
						// 	</Typography>
						// 	{/* <InputLabel id="demo-simple-select-helper-label">{localLang.PlotDimensions + "*"}</InputLabel> */}
						// 	<Select
						// 		labelId="demo-simple-select-helper-label"
						// 		id="demo-simple-select-helper"
						// 		multiple={propertyData.transaction_type === "buy" ? true : false}
						// 		value={property_plot_dimensions}
						// 		sx={{
						// 			width: "100%",
						// 			[theme.breakpoints.down("sm")]: {
						// 				minWidth: "100%",
						// 			},
						// 		}}
						// 		MenuProps={{
						// 			PaperProps: {
						// 				sx: {
						// 					"& .MuiMenu-list": {
						// 						direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						// 					},
						// 				},
						// 			},
						// 		}}
						// 		// label={localLang.PlotDimensions + "*"}
						// 		onChange={(event: any) => {
						// 			if (section_to_edit !== "property-config") {
						// 				dispatch(setSectionToEditThunk("property-config"));
						// 			}
						// 			if (propertyData.transaction_type !== "buy") {
						// 				if (event.target.value !== "Other") {
						// 					dispatch(
						// 						setSinglePropertyMinSuperBuiltUpAreaThunk(
						// 							Number(event.target.value.split(" x ")[0] * event.target.value.split(" x ")[1]),
						// 						),
						// 					);
						// 					dispatch(
						// 						setSinglePropertyMaxSuperBuiltUpAreaThunk(
						// 							Number(event.target.value.split(" x ")[0] * event.target.value.split(" x ")[1]),
						// 						),
						// 					);
						// 				} else {
						// 					dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(0));
						// 					dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(0));
						// 				}
						// 			}
						// 			dispatch(
						// 				setSinglePropertyPlotDimensionsThunk(
						// 					typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value,
						// 				),
						// 			);
						// 			setErrorPlotDimensions(false);
						// 		}}
						// 		input={<OutlinedInput label={localLang.PlotDimensions} />}
						// 		renderValue={(selected) =>
						// 			propertyData.transaction_type !== "buy" && selected[0] !== "Other" ? selected : selected.join(", ")
						// 		}
						// 	>
						// 		{plot_dimensions?.map((name) => (
						// 			<MenuItem
						// 				key={name}
						// 				value={name}
						// 			>
						// 				{propertyData.transaction_type === "buy" ? (
						// 					<Checkbox checked={property_plot_dimensions.indexOf(name) > -1} />
						// 				) : null}

						// 				<ListItemText primary={name} />
						// 			</MenuItem>
						// 		))}
						// 	</Select>
						// 	<FormHelperText>{errorPlotDimensions ? localLang.select_at_least_one_option : ""}</FormHelperText>
						// </FormControl>
						<FormControl
							sx={{
								width: "100%",
								margin: "0rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									margin: "0.5rem 0rem 0rem 0.5rem",
								},
							}}
							error={errorPlotDimensions}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<AspectRatioOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode === "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.PlotDimensions + "*"}
							</Typography>

							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
								{plot_dimensions.map((name, index) => {
									const isSelected = property_plot_dimensions.includes(name);
									const isMultiSelect = propertyData.transaction_type === "buy";

									const handleClick = () => {
										let updatedValues: string[];

										if (isMultiSelect) {
											updatedValues = isSelected
												? property_plot_dimensions.filter((val) => val !== name)
												: [...property_plot_dimensions, name];
										} else {
											updatedValues = isSelected ? [] : [name];

											// Set min/max area for non-buy
											if (name !== "Other") {
												const [width, height] = name.split(" x ").map(Number);
												const area = width * height;
												dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(area));
												dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(area));
											} else {
												dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(0));
												dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(0));
											}
										}

										// Dispatch updates
										dispatch(setSinglePropertyPlotDimensionsThunk(updatedValues));
										setErrorPlotDimensions(false);
										if (section_to_edit !== "property-config") {
											dispatch(setSectionToEditThunk("property-config"));
										}
									};

									return (
										<CustomChip
											key={index}
											disableRipple
											disableTouchRipple
											disableFocusRipple
											isSelected={isSelected}
											onClick={handleClick}
											variant="outlined"
										>
											<Text
												variant="body1"
												sx={{
													fontWeight: isSelected ? "500" : "400",
												}}
											>
												{name}
											</Text>
										</CustomChip>
									);
								})}
							</Box>

							<FormHelperText>{errorPlotDimensions ? localLang.select_at_least_one_option : ""}</FormHelperText>
						</FormControl>
					)}
				</TextContainer>
			) : null}

			{/* Other Plot Dimensions */}

			{propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			isEditing &&
			(propertyData.property_type.includes("Plot") ||
				propertyData.property_type.includes("Farm Plot") ||
				propertyData.property_type.includes("Plot (Commercial)")) &&
			property_plot_dimensions.includes("Other") ? (
				<TextContainer sx={{ padding: "0.5rem 0rem ", alignItems: "flex-start" }}>
					{!isEditing && (
						<AddBoxOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					<TextContainer
						sx={{
							width: "100%",
							flexDirection: "column",
							justifyContent: "flex-start",
							alignItems: "flex-start",
							margin: "0rem 0rem 0rem 0rem",
							[theme.breakpoints.down("sm")]: {
								margin: "0rem 0rem 0rem 0.5rem",
							},
						}}
					>
						<Typography
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "flex-start",
								alignItems: "center",
								gap: "0.5rem",
								fontFamily: "Poppins",
								fontSize: "14px",
								fontWeight: 500,
								lineHeight: "157%",
								letterSpacing: "0.1px",
							}}
						>
							<AddBoxOutlined
								sx={{
									fontSize: "1.25rem",
									[theme.breakpoints.up("md1190")]: {
										fontSize: "1.5rem",
									},
									color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
								}}
							/>
							{localLang.other_plot_dimensions + " : "}
						</Typography>

						{property_plot_dimensions.filter((item) => !plot_dimensions.includes(item)).length > 0
							? property_plot_dimensions
									.filter((item) => !plot_dimensions.includes(item))
									.map((item: string, index: number) => (
										<div key={index}>
											<TextContainer sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
												<TextField
													id={screenType + "_spp_config_and_area_other_plot_dimensions_edit_textfield"}
													value={item.split(" x ")[0] === "0" ? "" : item.split(" x ")[0]}
													onChange={(event: any) => {
														if (section_to_edit !== "property-config") {
															dispatch(setSectionToEditThunk("property-config"));
														}
														const { value } = event.target;
														const other_plot_dimensions = property_plot_dimensions.filter(
															(item) => !plot_dimensions.includes(item),
														);
														const list = [...other_plot_dimensions];
														const dimensions = list[index].split(" x ");
														dimensions[0] = Number(value.replace(/\D/g, "")).toString();
														list[index] = dimensions.join(" x ");
														dispatch(
															setSinglePropertyPlotDimensionsThunk([
																...property_plot_dimensions.filter((item) => plot_dimensions.includes(item)),
																...list,
															]),
														);
														if (propertyData.transaction_type !== "buy") {
															dispatch(
																setSinglePropertyMinSuperBuiltUpAreaThunk(
																	parseInt(value) *
																		(dimensions[1] && dimensions[1] !== "" ? parseInt(dimensions[1]) : 0),
																),
															);
															dispatch(
																setSinglePropertyMaxSuperBuiltUpAreaThunk(
																	parseInt(value) *
																		(dimensions[1] && dimensions[1] !== "" ? parseInt(dimensions[1]) : 0),
																),
															);
														}
														const newValue = Number(value.replace(/\D/g, ""));
														const newErrorMinOtherPlot = [...errorMinOtherPlot];
														if (isNaN(newValue) || newValue <= 0) {
															newErrorMinOtherPlot[index] = true;
															setSubmitDisabled(true);
														} else {
															newErrorMinOtherPlot[index] = false;
															setSubmitDisabled(false);
														}
														setErrorMinOtherPlot(newErrorMinOtherPlot);
													}}
													label={localLang.length}
													sx={{
														width: "45%",
													}}
													autoComplete="off"
													error={errorMinOtherPlot[index]}
													helperText={errorMinOtherPlot[index] ? localLang.enter_a_valid_number : ""}
													slotProps={{
														htmlInput: {
															inputMode: "numeric",
															pattern: "[0-9]*",
														},
													}}
													onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
														if (event.key === "Enter" || event.key === "Next") {
															(event.target as HTMLInputElement).blur();
														}
													}}
												/>

												<Typography
													variant="body1"
													sx={{ margin: "1rem 0rem 0rem 0rem" }}
												>
													x
												</Typography>

												<TextField
													id={screenType + "_spp_config_and_area_other_plot_dimensions_edit_textfield"}
													value={item.split(" x ")[1] === "0" ? "" : item.split(" x ")[1]}
													inputProps={{
														min: 0,
														inputMode: "numeric",
														pattern: "[0-9]*",
													}}
													onChange={(event: any) => {
														if (section_to_edit !== "property-config") {
															dispatch(setSectionToEditThunk("property-config"));
														}
														const { value } = event.target;
														const other_plot_dimensions = property_plot_dimensions.filter(
															(item) => !plot_dimensions.includes(item),
														);
														const list = [...other_plot_dimensions];
														const dimensions = list[index].split(" x ");
														dimensions[1] = Number(value.replace(/\D/g, "")).toString();
														list[index] = dimensions.join(" x ");
														dispatch(
															setSinglePropertyPlotDimensionsThunk([
																...property_plot_dimensions.filter((item) => plot_dimensions.includes(item)),
																...list,
															]),
														);
														if (propertyData.transaction_type !== "buy") {
															dispatch(
																setSinglePropertyMinSuperBuiltUpAreaThunk(
																	parseInt(value) *
																		(dimensions[0] && dimensions[0] !== "" ? parseInt(dimensions[0]) : 0),
																),
															);
															dispatch(
																setSinglePropertyMaxSuperBuiltUpAreaThunk(
																	parseInt(value) *
																		(dimensions[0] && dimensions[0] !== "" ? parseInt(dimensions[0]) : 0),
																),
															);
														}
														const newValue = Number(value.replace(/\D/g, ""));
														const newErrorMaxOtherPlot = [...errorMaxOtherPlot];
														if (isNaN(newValue) || newValue <= 0) {
															newErrorMaxOtherPlot[index] = true;
															setSubmitDisabled(true);
														} else {
															newErrorMaxOtherPlot[index] = false;
															setSubmitDisabled(false);
														}
														setErrorMaxOtherPlot(newErrorMaxOtherPlot);
													}}
													label={localLang.width}
													sx={{
														width: "45%",
													}}
													autoComplete="off"
													error={errorMaxOtherPlot[index]}
													helperText={errorMaxOtherPlot[index] ? localLang.enter_a_valid_number : ""}
												/>
												{propertyData.transaction_type === "buy" ? (
													<IconButton
														id={screenType + "_spp_config_and_area_other_plot_dimensions_delete_button"}
														color="primary"
														sx={{ margin: "0.5rem 0rem 0rem 0rem" }}
														onClick={() => {
															if (section_to_edit !== "property-config") {
																dispatch(setSectionToEditThunk("property-config"));
															}
															const other_plot_dimensions = property_plot_dimensions.filter(
																(item) => !plot_dimensions.includes(item),
															);
															const list = [...other_plot_dimensions];
															list.splice(index, 1);
															dispatch(
																setSinglePropertyPlotDimensionsThunk([
																	...property_plot_dimensions.filter((item) => plot_dimensions.includes(item)),
																	...list,
																]),
															);
														}}
													>
														<DeleteOutline />
													</IconButton>
												) : null}
											</TextContainer>

											{property_plot_dimensions.filter((item) => !plot_dimensions.includes(item)).length - 1 ===
												index && propertyData.transaction_type === "buy" ? (
												<IconButton
													id={screenType + "_spp_config_and_area_other_plot_dimensions_add_more_button"}
													color="primary"
													onClick={() => {
														if (section_to_edit !== "property-config") {
															dispatch(setSectionToEditThunk("property-config"));
														}
														dispatch(setSinglePropertyPlotDimensionsThunk([...property_plot_dimensions, ""]));
													}}
													sx={{
														fontSize: "0.875rem",
														borderRadius: "0.5rem",
													}}
												>
													<Add sx={{ margin: "0rem 0.25rem 0rem 0rem" }} />
													{localLang.addMore}
												</IconButton>
											) : null}
										</div>
									))
							: dispatch(setSinglePropertyPlotDimensionsThunk([...property_plot_dimensions, ""]))}

						{/* <TextField
							value={other_plot_dimensions}
							onChange={(event: any) => {
								if (section_to_edit !== "property-config") {
									dispatch(setSectionToEditThunk("property-config"));
								}
								dispatch(
									setSinglePropertyOtherPlotDimensionsThunk(
										// On autofill we get a stringified value.
										typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value,
									),
								);
							}}
							label="Enter other plot dimensions"
							sx={{
								width: "100%",
								[theme.breakpoints.down("sm")]: {
									margin: "0rem 0rem 0rem 0.5rem",
									minWidth: "100%",
								},
							}}
							placeholder="Ex: 24x33, 42x69, 87x85"
							helperText="Enter values separated by comma"
							inputProps={{
									min: 0,
									inputMode: "numeric",
									pattern: "[0-9]*",
								}}
						/> */}
					</TextContainer>
				</TextContainer>
			) : null}

			{/* Total land Area */}

			{propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			((propertyData.transaction_type == "rent" &&
				(propertyData.property_type.includes("Land (Residential)") ||
					propertyData.property_type.includes("Land (Commercial)") ||
					propertyData.property_type.includes("Land (Agricultural)") ||
					propertyData.property_type.includes("Land (Industrial)") ||
					propertyData.property_type.includes("Mining"))) ||
				(propertyData.transaction_type === "resale" && propertyData.property_type.includes("Building (Residential)")) ||
				propertyData.transaction_type === "buy") ? (
				<TextContainer sx={{ padding: "0.5rem 0rem", lignItems: "flex-start" }}>
					{!isEditing && (
						<SvgIcon
							inheritViewBox={false}
							component={LandAreaIcon}
							viewBox="0 0 24 24"
							width={24}
							height={24}
							sx={{
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.totalLandArea + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_area.land_area.max.acre <= 0.5
									? property_area.land_area.max.ft.toFixed(0) + " " + localLang.sqft
									: property_area.land_area.max.acre % 1 === 0
										? property_area.land_area.max.acre.toFixed(0) + " " + localLang.acre
										: property_area.land_area.max.acre.toFixed(2) + " " + localLang.acre}
							</CustomText>
						</DetailsContainer>
					) : (
						<TextContainer
							sx={{
								// 	margin: "0.5rem 0rem 0rem 0rem",
								// 	[theme.breakpoints.down("sm")]: {
								// 		margin: "0.5rem 0rem 0rem 0.5rem",
								// 	},
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<SvgIcon
									inheritViewBox={false}
									component={LandAreaIcon}
									viewBox="0 0 24 24"
									width={24}
									height={24}
									sx={{
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
								{localLang.totalLandArea + " : "}
							</Typography>
							<TextField
								id={screenType + "_spp_config_and_area_land_area_edit_textfield"}
								value={property_area.land_area.max.acre === 0 ? "" : property_area.land_area.max.acre}
								sx={{
									width: "100%",
								}}
								autoComplete="off"
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyLandAreaThunk(event.target.value.replace(/[^0-9.]/g, "")));
									if (
										(propertyData.transaction_type == "resale" ||
											propertyData.transaction_type == "buy" ||
											(propertyData.transaction_type == "rent" &&
												(propertyData.property_type.includes("Land (Residential)") ||
													propertyData.property_type.includes("Land (Commercial)") ||
													propertyData.property_type.includes("Land (Agricultural)") ||
													propertyData.property_type.includes("Land (Industrial)") ||
													propertyData.property_type.includes("Plot") ||
													propertyData.property_type.includes("Farm Plot") ||
													propertyData.property_type.includes("Plot (Commercial)")))) &&
										isNaN(
											// Number(
											event.target.value.replace(/[^0-9.]/g, ""),
											// )
										)
									) {
										setErrorLandArea(true);
										setSubmitDisabled(true);
									} else {
										setErrorLandArea(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorLandArea}
								helperText={errorLandArea ? localLang.enter_a_valid_number : ""}
								label={
									propertyData.transaction_type === "buy" ||
									(propertyData.transaction_type === "rent" &&
										(propertyData.property_type.includes("Land (Residential)") ||
											propertyData.property_type.includes("Land (Commercial)") ||
											propertyData.property_type.includes("Land (Agricultural)") ||
											propertyData.property_type.includes("Land (Industrial)") ||
											propertyData.property_type.includes("Mining")))
										? localLang.land_area + "*"
										: localLang.land_area
								}
							/>

							<FormControl
								sx={{
									width: "100%",
									margin: "0.5rem 0rem 0rem 0rem",
								}}
							>
								<InputLabel id={screenType + "_demo-simple-select-helper-label"}>{localLang.unit}</InputLabel>
								<Select
									labelId={"demo-simple-select-helper-label"}
									id={screenType + "_demo-simple-select-helper"}
									value={landAreaUnit}
									sx={{
										width: "100%",
										height: "3.5rem",
									}}
									MenuProps={{
										PaperProps: {
											sx: {
												"& .MuiMenu-list": {
													direction: router.locale === "ar-AE" ? "rtl" : "ltr",
												},
											},
										},
									}}
									label={localLang.unit}
									onChange={(event: any) => {
										if (section_to_edit !== "property-config") {
											dispatch(setSectionToEditThunk("property-config"));
										}
										setLandAreaUnit(event.target.value);
									}}
								>
									<MenuItem value="acre">{localLang.acre}</MenuItem>
									<MenuItem value="sqft">{localLang.sqft}</MenuItem>
									<MenuItem value="sqmt">{localLang.sqm}</MenuItem>
								</Select>
							</FormControl>
						</TextContainer>
					)}
				</TextContainer>
			) : null}

			{/* Super Built Up Area */}

			{propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Mining") ? (
				<TextContainer
					sx={{
						padding: "0.5rem 0rem",
						alignItems: "flex-start",
					}}
				>
					{!isEditing && (
						<SvgIcon
							inheritViewBox={false}
							component={SuperBuiltAreaIcon}
							viewBox="0 0 24 24"
							width={24}
							height={24}
							sx={{
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.plotArea + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{propertyData.area.super_built_up_area.min.ft === propertyData.area.super_built_up_area.max.ft
									? propertyData.area.super_built_up_area.max.ft > 0
										? propertyData.area.super_built_up_area.max.ft.toFixed(0) + " " + localLang.sqft
										: "(not filled)"
									: (propertyData.area.super_built_up_area.min.ft > 0
											? propertyData.area.super_built_up_area.min.ft.toFixed(0) + " " + localLang.sqft + " - "
											: "(not filled)") +
										(propertyData.area.super_built_up_area.max.ft > 0
											? propertyData.area.super_built_up_area.max.ft.toFixed(0) + " " + localLang.sqft
											: "(not filled)")}
							</CustomText>
						</DetailsContainer>
					) : (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Independent House")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Co-living/PG")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Building (Commercial)")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Building (Residential)")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Shop")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Office Space")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Co-working")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Warehouse")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Factory")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Independent House")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Building (Commercial)")) ||
					  (propertyData.transaction_type == "rent" &&
							propertyData.property_type.includes("Building (Residential)")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Shop")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Apartment")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Villa")) ||
					  (propertyData.transaction_type == "rent" &&
							propertyData.property_type.includes("Builder Floor Apartment")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Penthouse")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Row House")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Plot")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Farm Plot")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Plot (Commercial)")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Farm House")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Warehouse")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Factory")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Independent House")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Co-living/PG")) ||
					  (propertyData.transaction_type == "resale" &&
							propertyData.property_type.includes("Building (Commercial)")) ||
					  (propertyData.transaction_type == "resale" &&
							propertyData.property_type.includes("Building (Residential)")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Shop")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Office Space")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Co-working")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Plot")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Farm Plot")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Plot (Commercial)")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Farm House")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Warehouse")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Apartment")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Villa")) ||
					  (propertyData.transaction_type == "resale" &&
							propertyData.property_type.includes("Builder Floor Apartment")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Penthouse")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Row House")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Factory")) ? (
						<TextContainer
							sx={{
								// 	margin: "0.5rem 0rem 0rem 0rem",
								// 	[theme.breakpoints.down("sm")]: {
								// 		margin: "0.5rem 0rem 0rem 0.5rem",
								// 	},
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<SvgIcon
									inheritViewBox={false}
									component={SuperBuiltAreaIcon}
									viewBox="0 0 24 24"
									width={24}
									height={24}
									sx={{
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
								{propertyData.property_type.includes("Plot") ||
								propertyData.property_type.includes("Farm Plot") ||
								propertyData.property_type.includes("Plot (Commercial)")
									? localLang.plotArea + " :"
									: localLang.plotArea + " :"}
							</Typography>
							<TextField
								id={screenType + "_spp_config_and_area_super_built_up_area_edit_textfield"}
								value={
									property_area.super_built_up_area.max.ft === 0 || isNaN(property_area.super_built_up_area.max.ft)
										? ""
										: property_area.super_built_up_area.max.ft
								}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								sx={{
									width: "100%",
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(Number(event.target.value.replace(/\D/g, ""))));
									dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (isNaN(Number(event.target.value.replace(/\D/g, "")))) {
										setErrorSba(true);
										setSubmitDisabled(true);
									} else {
										setErrorSba(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorSba}
								helperText={errorSba ? localLang.enter_a_valid_number : ""}
								label={localLang.area + "*"}
							/>
							<FormControl
								sx={{
									width: "100%",
									margin: "0.5rem 0rem 0rem 0rem",
								}}
							>
								<InputLabel id={screenType + "_demo-simple-select-helper-label"}>{localLang.unit}</InputLabel>
								<Select
									labelId={"_demo-simple-select-helper-label"}
									id={screenType + "_demo-simple-select-helper"}
									value={superBuiltUpAreaUnit}
									sx={{
										width: "100%",
										height: "3.5rem",
									}}
									MenuProps={{
										PaperProps: {
											sx: {
												"& .MuiMenu-list": {
													direction: router.locale === "ar-AE" ? "rtl" : "ltr",
												},
											},
										},
									}}
									label={localLang.unit}
									onChange={(event: any) => {
										if (section_to_edit !== "property-config") {
											dispatch(setSectionToEditThunk("property-config"));
										}
										setSuperBuiltUpAreaUnit(event.target.value);
										setCarpetAreaUnit(event.target.value);
									}}
								>
									<MenuItem value="sqft">{localLang.sqft}</MenuItem>
									<MenuItem value="sqmt">{localLang.sqm}</MenuItem>
								</Select>
							</FormControl>
						</TextContainer>
					) : (
						<TextContainer
							sx={{
								// 	margin: "0.5rem 0rem 0rem 0rem",
								// 	[theme.breakpoints.down("sm")]: {
								// 		margin: "0.5rem 0rem 0rem 0.5rem",
								// 	},
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<SvgIcon
									inheritViewBox={false}
									component={SuperBuiltAreaIcon}
									viewBox="0 0 24 24"
									width={24}
									height={24}
									sx={{
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
								{propertyData.property_type.includes("Plot") ||
								propertyData.property_type.includes("Farm Plot") ||
								propertyData.property_type.includes("Plot (Commercial)")
									? localLang.plotArea + " :"
									: localLang.plotArea + " :"}
							</Typography>
							<TextField
								id={screenType + "_spp_config_and_area_super_built_up_area_edit_textfield"}
								value={
									property_area.super_built_up_area.min.ft === 0 || isNaN(property_area.super_built_up_area.min.ft)
										? ""
										: property_area.super_built_up_area.min.ft
								}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								sx={{
									width: "100%",
								}}
								autoComplete="off"
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (
										(property_area.super_built_up_area.max.ft !== 0 &&
											Number(event.target.value.replace(/\D/g, "")) > property_area.super_built_up_area.max.ft) ||
										isNaN(Number(event.target.value.replace(/\D/g, "")))
									) {
										setErrorMinSba(true);
										setErrorMaxSba(true);
										setSubmitDisabled(true);
									} else {
										setErrorMinSba(false);
										setErrorMaxSba(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorMinSba}
								helperText={errorMinSba ? localLang.minimum_value_is_more_than_maximum_value : ""}
								label={localLang.minimum_area + "*"}
							/>

							<TextField
								id={screenType + "_spp_config_and_area_super_built_up_area_edit_textfield"}
								value={
									property_area.super_built_up_area.max.ft === 0 || isNaN(property_area.super_built_up_area.max.ft)
										? ""
										: property_area.super_built_up_area.max.ft
								}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								sx={{
									width: "100%",
									margin: "0.5rem 0rem 0rem 0rem",
								}}
								autoComplete="off"
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (
										(property_area.super_built_up_area.min.ft !== 0 &&
											Number(event.target.value.replace(/\D/g, "")) < property_area.super_built_up_area.min.ft) ||
										isNaN(Number(event.target.value.replace(/\D/g, "")))
									) {
										setErrorMinSba(true);
										setErrorMaxSba(true);
										setSubmitDisabled(true);
									} else {
										setErrorMinSba(false);
										setErrorMaxSba(false);
										setSubmitDisabled(false);
									}
								}}
								error={errorMaxSba}
								helperText={errorMaxSba ? localLang.maximum_value_is_less_than_minimum_value : ""}
								label={localLang.maximum_area + "*"}
							/>

							<FormControl
								sx={{
									width: "100%",
									margin: "0.5rem 0rem 0rem 0rem",
								}}
							>
								<InputLabel id={screenType + "_demo-simple-select-helper-label"}>{localLang.unit}</InputLabel>
								<Select
									labelId={"demo-simple-select-helper-label"}
									id={screenType + "_demo-simple-select-helper"}
									value={superBuiltUpAreaUnit}
									sx={{
										width: "100%",
										height: "3.5rem",
									}}
									MenuProps={{
										PaperProps: {
											sx: {
												"& .MuiMenu-list": {
													direction: router.locale === "ar-AE" ? "rtl" : "ltr",
												},
											},
										},
									}}
									label={localLang.unit}
									onChange={(event: any) => {
										if (section_to_edit !== "property-config") {
											dispatch(setSectionToEditThunk("property-config"));
										}
										setSuperBuiltUpAreaUnit(event.target.value);
										setCarpetAreaUnit(event.target.value);
									}}
								>
									<MenuItem value="sqft">{localLang.sqft}</MenuItem>
									<MenuItem value="sqmt">{localLang.sqm}</MenuItem>
								</Select>
							</FormControl>
						</TextContainer>
					)}
				</TextContainer>
			) : null}

			{/* Carpet area is % of super built up area */}

			{/* {propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			isEditing &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Plot") &&
			!propertyData.property_type.includes("Farm Plot") &&
			!propertyData.property_type.includes("Plot (Commercial)") &&
			!propertyData.property_type.includes("Co-living/PG") &&
			!propertyData.property_type.includes("Warehouse") &&
			!propertyData.property_type.includes("Mining") ? (
				<TextContainer sx={{ margin: "0.5rem 0rem 0rem 0rem" }}>
					<PercentOutlined
						sx={{
							color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
						}}
					/>
					<FormGroup
						sx={{
							margin: "0rem 0rem 0rem 0rem",
							width: "100%",
						}}
					>
						<FormControlLabel
							labelPlacement="start"
							control={
								<Switch
									checked={ca_is_percentage_of_sba}
									onChange={(event: any) => {
										if (section_to_edit !== "property-config") {
											dispatch(setSectionToEditThunk("property-config"));
										}
										setCaIsPercentageOfSba(event.target.checked);
									}}
								/>
							}
							sx={{
								display: "flex",
								flexDirection: "row-reverse",
								justifyContent: "space-between",
								alignItems: "center",
								width: "100%",
								margin: "0rem 0rem 0rem 0rem",
								"& .MuiFormControlLabel-label": {
									fontSize: "0.875rem",
								},
							}}
							label="Carpet area is % of super built up area"
						/>
					</FormGroup>
				</TextContainer>
			) : null} */}

			{/* Carpet Area */}

			{/* {propertyData.transaction_type !== "ofb" &&
			propertyData.transaction_type !== "" &&
			propertyData.property_category !== "" &&
			propertyData.property_type.length !== 0 &&
			!propertyData.property_type.includes("Land (Residential)") &&
			!propertyData.property_type.includes("Land (Commercial)") &&
			!propertyData.property_type.includes("Land (Agricultural)") &&
			!propertyData.property_type.includes("Land (Industrial)") &&
			!propertyData.property_type.includes("Plot") &&
			!propertyData.property_type.includes("Farm Plot") &&
			!propertyData.property_type.includes("Plot (Commercial)") &&
			!propertyData.property_type.includes("Co-living/PG") &&
			!propertyData.property_type.includes("Warehouse") &&
			!propertyData.property_type.includes("Factory") &&
			!propertyData.property_type.includes("Mining") ? (
				<TextContainer sx={{ alignItems: "flex-start" }}>
					<SvgIcon
						inheritViewBox={false}
						viewBox="0 0 24 24"
						width={24}
						height={24}
						sx={{
							margin: "0.35rem 0rem 0rem 0rem",
							color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							[theme.breakpoints.down("sm")]: {
								margin: "0.3rem 0rem 0rem 0rem",
							},
						}}
					>
						{theme.palette.mode === "dark" ? <CarpetAreaWhiteIcon /> : <CarpetAreaIcon />}
					</SvgIcon>
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{"Carpet area :"}</CustomText>
							<CustomText>
								{propertyData.area.carpet_area.min.ft === propertyData.area.carpet_area.max.ft
									? propertyData.area.carpet_area.max.ft > 0
										? propertyData.area.carpet_area.max.ft.toFixed(0) + " sqft"
										: ""
									: (propertyData.area.carpet_area.min.ft > 0
											? propertyData.area.carpet_area.min.ft.toFixed(0) + " sqft - "
											: "") +
										(propertyData.area.carpet_area.max.ft > 0
											? propertyData.area.carpet_area.max.ft.toFixed(0) + " sqft"
											: "")}
							</CustomText>
						</DetailsContainer>
					) : (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Independent House")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Building")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Shop")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Office Space")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Co-working")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Warehouse")) ||
					  (propertyData.transaction_type == "buy" && propertyData.property_type.includes("Factory")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Independent House")) ||
					  (propertyData.transaction_type == "rent" &&
							propertyData.property_type.includes("Builder Floor Apartment")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Farm House")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Villa")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Apartment")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Warehouse")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Factory")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Building")) ||
					  (propertyData.transaction_type == "rent" && propertyData.property_type.includes("Shop")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Independent House")) ||
					  (propertyData.transaction_type == "resale" &&
							propertyData.property_type.includes("Builder Floor Apartment")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Building")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Shop")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Office Space")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Co-working")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Farm House")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Warehouse")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Apartment")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Villa")) ||
					  (propertyData.transaction_type == "resale" && propertyData.property_type.includes("Factory")) ? (
						<TextContainer
							sx={{
								width: "100%",
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
								margin: "0rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									margin: "0rem 0rem 0rem 0.5rem",
								},
							}}
						>
							<CustomText
								sx={{
									margin: "0.35rem 0rem 0.75rem 0rem",
									[theme.breakpoints.down("sm")]: {
										margin: "0.3rem 0rem 0.75rem 0rem",
									},
								}}
							>
								Carpet area :{" "}
							</CustomText>
							<TextField
								value={
									ca_is_percentage_of_sba
										? maxCarpetAreaPercentage === 0
											? ""
											: maxCarpetAreaPercentage
										: property_area.carpet_area.max.ft === 0
											? ""
											: property_area.carpet_area.max.ft
								}
								inputProps={{
									min: 0,
									inputMode: "numeric",
									pattern: "[0-9]*",
								}}
								sx={{
									width: "100%",
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									setMinCarpetAreaPercentage(Number(event.target.value.replace(/\D/g, "")));
									setMaxCarpetAreaPercentage(Number(event.target.value.replace(/\D/g, "")));

									if (ca_is_percentage_of_sba) {
										dispatch(
											setSinglePropertyMinCarpetAreaThunk(
												parseFloat(
													(
														property_area.super_built_up_area.min.ft *
														(Number(event.target.value.replace(/\D/g, "")) / 100)
													).toFixed(2),
												),
											),
										);
										dispatch(
											setSinglePropertyMaxCarpetAreaThunk(
												parseFloat(
													(
														property_area.super_built_up_area.max.ft *
														(Number(event.target.value.replace(/\D/g, "")) / 100)
													).toFixed(2),
												),
											),
										);
									} else {
										dispatch(setSinglePropertyMinCarpetAreaThunk(Number(event.target.value.replace(/\D/g, ""))));
										dispatch(setSinglePropertyMaxCarpetAreaThunk(Number(event.target.value.replace(/\D/g, ""))));
									}
								}}
								autoComplete="off"
								label={ca_is_percentage_of_sba ? "Percentage (%)" : "Area"}
							/>
						</TextContainer>
					) : (
						<TextContainer
							sx={{
								width: "100%",
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
								margin: "0rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									margin: "0rem 0rem 0rem 0.5rem",
								},
							}}
						>
							<CustomText
								sx={{
									margin: "0.3rem 0rem 0.75rem 0rem",
									[theme.breakpoints.down("sm")]: {
										margin: "0.3rem 0rem 0.75rem 0rem",
									},
								}}
							>
								Carpet area :{" "}
							</CustomText>
							<TextField
								value={
									ca_is_percentage_of_sba
										? minCarpetAreaPercentage === 0
											? ""
											: minCarpetAreaPercentage
										: property_area.carpet_area.min.ft === 0
											? ""
											: property_area.carpet_area.min.ft
								}
								inputProps={{
									min: 0,
									inputMode: "numeric",
									pattern: "[0-9]*",
								}}
								sx={{
									width: "100%",
								}}
								onChange={(event: any) => {
									if (section_to_edit !== "property-config") {
										dispatch(setSectionToEditThunk("property-config"));
									}
									setMinCarpetAreaPercentage(Number(event.target.value.replace(/\D/g, "")));
									setMaxCarpetAreaPercentage(Number(event.target.value.replace(/\D/g, "")));
									if (ca_is_percentage_of_sba) {
										dispatch(
											setSinglePropertyMinCarpetAreaThunk(
												parseFloat(
													(
														property_area.super_built_up_area.min.ft *
														(Number(event.target.value.replace(/\D/g, "")) / 100)
													).toFixed(2),
												),
											),
										);
										dispatch(
											setSinglePropertyMaxCarpetAreaThunk(
												parseFloat(
													(
														property_area.super_built_up_area.max.ft *
														(Number(event.target.value.replace(/\D/g, "")) / 100)
													).toFixed(2),
												),
											),
										);
									} else {
										dispatch(setSinglePropertyMinCarpetAreaThunk(Number(event.target.value.replace(/\D/g, ""))));
										if (
											property_area.carpet_area.max.ft !== 0 &&
											Number(event.target.value.replace(/\D/g, "")) > property_area.carpet_area.max.ft
										) {
											setErrorMinCa(true);
											setErrorMaxCa(true);
											setSubmitDisabled(true);
										} else {
											setErrorMinCa(false);
											setErrorMaxCa(false);
											setSubmitDisabled(false);
										}
									}
								}}
								error={errorMinCa}
								autoComplete="off"
								helperText={errorMinCa ? "Value is more than max area" : ""}
								label={ca_is_percentage_of_sba ? "Percentage (%)" : "Minimum Area"}
							/>

							{ca_is_percentage_of_sba ? null : (
								<TextField
									value={
										ca_is_percentage_of_sba
											? maxCarpetAreaPercentage === 0
												? ""
												: maxCarpetAreaPercentage
											: property_area.carpet_area.max.ft === 0
												? ""
												: property_area.carpet_area.max.ft
									}
									inputProps={{
										min: 0,
									}}
									sx={{
										width: "100%",
										margin: "0.5rem 0rem 0rem 0rem",
									}}
									onChange={(event: any) => {
										if (section_to_edit !== "property-config") {
											dispatch(setSectionToEditThunk("property-config"));
										}
										// setMaxCarpetAreaPercentage(event.target.value);
										// if (ca_is_percentage_of_sba) {
										//   setMaxCarpetArea(
										//     (
										//       property_area.super_built_up_area.max.ft *
										//       (event.target.value / 100)
										//     ).toFixed(2)
										//   );
										// } else {
										dispatch(setSinglePropertyMaxCarpetAreaThunk(Number(event.target.value.replace(/\D/g, ""))));
										// }
										if (
											property_area.carpet_area.min.ft !== 0 &&
											Number(event.target.value.replace(/\D/g, "")) < property_area.carpet_area.min.ft
										) {
											setErrorMinCa(true);
											setErrorMaxCa(true);
											setSubmitDisabled(true);
										} else {
											setErrorMinCa(false);
											setErrorMaxCa(false);
											setSubmitDisabled(false);
										}
									}}
									error={errorMaxCa}
									autoComplete="off"
									helperText={errorMaxCa ? "Value is less than min area" : ""}
									label="Maximum Area"
								/>
							)}
						</TextContainer>
					)}
				</TextContainer>
			) : null} */}

			{/* Update Button */}

			{isEditing ? (
				<>
					{load ? (
						<CircularProgress
							// size={24}
							sx={{
								marginTop: "1rem",
							}}
						/>
					) : (
						<BeegruButton
							id={screenType + "_spp_config_and_area_update_button"}
							flavor="primary"
							onClick={() => {
								if (
									/* BHK */

									(!propertyData.property_type.includes("Plot") &&
										!propertyData.property_type.includes("Farm Plot") &&
										!propertyData.property_type.includes("Plot (Commercial)") &&
										!propertyData.property_type.includes("Land (Residential)") &&
										!propertyData.property_type.includes("Land (Commercial)") &&
										!propertyData.property_type.includes("Land (Agricultural)") &&
										!propertyData.property_type.includes("Land (Industrial)") &&
										!propertyData.property_type.includes("Studio Apartment") &&
										!propertyData.property_type.includes("Building (Commercial)") &&
										!propertyData.property_type.includes("Building (Residential)") &&
										!propertyData.property_type.includes("Shop") &&
										!propertyData.property_type.includes("Office Space") &&
										!propertyData.property_type.includes("Co-working") &&
										!propertyData.property_type.includes("Warehouse") &&
										!propertyData.property_type.includes("Factory") &&
										!propertyData.property_type.includes("Mining") &&
										(propertyData.bhk.min === 0 ||
											propertyData.bhk.min === undefined ||
											propertyData.bhk.max === 0 ||
											propertyData.bhk.max === null)) ||
									/* No. of seats */

									(propertyData.property_type.includes("Co-working") &&
										(propertyData.no_of_seats.max === 0 || propertyData.no_of_seats.max === null)) ||
									/* No. of units */

									(((propertyData.transaction_type === "buy" && propertyData.property_type.includes("Apartment")) ||
										(propertyData.transaction_type === "buy" && propertyData.property_type.includes("Plot")) ||
										(propertyData.transaction_type === "buy" && propertyData.property_type.includes("Farm Plot")) ||
										(propertyData.transaction_type === "buy" &&
											propertyData.property_type.includes("Plot (Commercial)")) ||
										(propertyData.transaction_type === "buy" && propertyData.property_type.includes("Villa")) ||
										(propertyData.transaction_type === "buy" && propertyData.property_type.includes("Row House")) ||
										(propertyData.transaction_type === "buy" &&
											propertyData.property_type.includes("Builder Floor Apartment")) ||
										(propertyData.transaction_type === "buy" &&
											propertyData.property_type.includes("Building (Residential)")) ||
										(propertyData.transaction_type === "buy" &&
											propertyData.property_type.includes("Studio Apartment")) ||
										(propertyData.transaction_type === "buy" && propertyData.property_type.includes("Penthouse")) ||
										(propertyData.transaction_type === "rent" &&
											propertyData.property_type.includes("Building (Residential)")) ||
										(propertyData.transaction_type === "resale" &&
											propertyData.property_type.includes("Building (Residential)"))) &&
										(propertyData.no_of_units.max === 0 || propertyData.no_of_units.max === null)) ||
									/* Plot Dimensions */

									((propertyData.property_type.includes("Plot") ||
										propertyData.property_type.includes("Farm Plot") ||
										propertyData.property_type.includes("Plot (Commercial)")) &&
										propertyData.plot_dimensions.length === 0) ||
									/* Land Area */

									((propertyData.transaction_type == "buy" ||
										(propertyData.transaction_type == "rent" &&
											(propertyData.property_type.includes("Land (Residential)") ||
												propertyData.property_type.includes("Land (Commercial)") ||
												propertyData.property_type.includes("Land (Agricultural)") ||
												propertyData.property_type.includes("Land (Industrial)") ||
												propertyData.property_type.includes("Mining")))) &&
										(propertyData.area.land_area.max.acre === 0 || propertyData.area.land_area.max.acre === null)) ||
									/* Super Built Up Area */

									(((propertyData.transaction_type == "buy" &&
										propertyData.property_type.includes("Independent House")) ||
										(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Apartment")) ||
										(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Co-living/PG")) ||
										(propertyData.transaction_type == "buy" &&
											propertyData.property_type.includes("Building (Commercial)")) ||
										(propertyData.transaction_type == "buy" &&
											propertyData.property_type.includes("Building (Residential)")) ||
										(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Shop")) ||
										(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Office Space")) ||
										(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Co-working")) ||
										(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Warehouse")) ||
										(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Factory")) ||
										(propertyData.transaction_type == "rent" &&
											propertyData.property_type.includes("Independent House")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Apartment")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Villa")) ||
										(propertyData.transaction_type == "rent" &&
											propertyData.property_type.includes("Builder Floor Apartment")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Penthouse")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Row House")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Plot")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Farm Plot")) ||
										(propertyData.transaction_type == "rent" &&
											propertyData.property_type.includes("Plot (Commercial)")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Farm House")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Warehouse")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Factory")) ||
										(propertyData.transaction_type == "rent" &&
											propertyData.property_type.includes("Building (Commercial)")) ||
										(propertyData.transaction_type == "rent" &&
											propertyData.property_type.includes("Building (Residential)")) ||
										(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Shop")) ||
										(propertyData.transaction_type == "resale" &&
											propertyData.property_type.includes("Independent House")) ||
										(propertyData.transaction_type == "resale" &&
											propertyData.property_type.includes("Co-living/PG")) ||
										(propertyData.transaction_type == "resale" &&
											propertyData.property_type.includes("Building (Commercial)")) ||
										(propertyData.transaction_type == "resale" &&
											propertyData.property_type.includes("Building (Residential)")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Shop")) ||
										(propertyData.transaction_type == "resale" &&
											propertyData.property_type.includes("Office Space")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Co-working")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Plot")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Farm Plot")) ||
										(propertyData.transaction_type == "resale" &&
											propertyData.property_type.includes("Plot (Commercial)")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Farm House")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Warehouse")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Apartment")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Villa")) ||
										(propertyData.transaction_type == "resale" &&
											propertyData.property_type.includes("Builder Floor Apartment")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Penthouse")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Row House")) ||
										(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Factory"))) &&
										(property_area.super_built_up_area.min.ft === 0 ||
											property_area.super_built_up_area.min.ft === null ||
											property_area.super_built_up_area.max.ft === 0 ||
											property_area.super_built_up_area.max.ft === null))
								) {
									if (
										!propertyData.property_type.includes("Plot") &&
										!propertyData.property_type.includes("Farm Plot") &&
										!propertyData.property_type.includes("Plot (Commercial)") &&
										!propertyData.property_type.includes("Land (Residential)") &&
										!propertyData.property_type.includes("Land (Commercial)") &&
										!propertyData.property_type.includes("Land (Agricultural)") &&
										!propertyData.property_type.includes("Land (Industrial)") &&
										!propertyData.property_type.includes("Studio Apartment") &&
										!propertyData.property_type.includes("Building (Commercial)") &&
										!propertyData.property_type.includes("Building (Residential)") &&
										!propertyData.property_type.includes("Shop") &&
										!propertyData.property_type.includes("Office Space") &&
										!propertyData.property_type.includes("Co-working") &&
										!propertyData.property_type.includes("Warehouse") &&
										!propertyData.property_type.includes("Factory") &&
										!propertyData.property_type.includes("Mining") &&
										(propertyData.bhk.min === 0 ||
											propertyData.bhk.min === undefined ||
											propertyData.bhk.max === 0 ||
											propertyData.bhk.max === null)
									) {
										if (propertyData.bhk.min === 0 || propertyData.bhk.min === undefined) {
											setErrorMinBedroom(true);
										}
										if (propertyData.bhk.max === 0 || propertyData.bhk.max === null) {
											setErrorMaxBedroom(true);
										}
										setErrorBedroom(true);
									}
									if (
										propertyData.property_type.includes("Co-working") &&
										(propertyData.no_of_seats.max === 0 || propertyData.no_of_seats.max === null)
									) {
										setErrorNoOfSeats(true);
									}
									if (
										(propertyData.transaction_type === "buy" &&
											(propertyData.property_type.includes("Apartment") ||
												propertyData.property_type.includes("Plot") ||
												propertyData.property_type.includes("Farm Plot") ||
												propertyData.property_type.includes("Plot (Commercial)") ||
												propertyData.property_type.includes("Villa") ||
												propertyData.property_type.includes("Row House") ||
												propertyData.property_type.includes("Builder Floor Apartment") ||
												propertyData.property_type.includes("Building (Residential)") ||
												propertyData.property_type.includes("Studio Apartment") ||
												propertyData.property_type.includes("Penthouse")) &&
											(propertyData.no_of_units.max === 0 || propertyData.no_of_units.max === null)) ||
										(propertyData.transaction_type === "rent" &&
											propertyData.property_type.includes("Building (Residential)") &&
											(propertyData.no_of_units.max === 0 || propertyData.no_of_units.max === null)) ||
										(propertyData.transaction_type === "resale" &&
											propertyData.property_type.includes("Building (Residential)") &&
											(propertyData.no_of_units.max === 0 || propertyData.no_of_units.max === null))
									) {
										setErrorNoOfUnits(true);
									}
									if (
										(propertyData.property_type.includes("Plot") ||
											propertyData.property_type.includes("Farm Plot") ||
											propertyData.property_type.includes("Plot (Commercial)")) &&
										propertyData.plot_dimensions.length === 0
									) {
										setErrorPlotDimensions(true);
									}
									if (
										(propertyData.transaction_type == "buy" &&
											(propertyData.area.land_area.max.acre === 0 || propertyData.area.land_area.max.acre === null)) ||
										(propertyData.transaction_type == "rent" &&
											(propertyData.property_type.includes("Land (Residential)") ||
												propertyData.property_type.includes("Land (Commercial)") ||
												propertyData.property_type.includes("Land (Agricultural)") ||
												propertyData.property_type.includes("Land (Industrial)") ||
												propertyData.property_type.includes("Mining")) &&
											(propertyData.area.land_area.max.acre === 0 || propertyData.area.land_area.max.acre === null))
									) {
										setErrorLandArea(true);
									}
									if (
										((propertyData.transaction_type == "buy" &&
											propertyData.property_type.includes("Independent House")) ||
											(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Apartment")) ||
											(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Co-living/PG")) ||
											(propertyData.transaction_type == "buy" &&
												propertyData.property_type.includes("Building (Commercial)")) ||
											(propertyData.transaction_type == "buy" &&
												propertyData.property_type.includes("Building (Residential)")) ||
											(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Shop")) ||
											(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Office Space")) ||
											(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Co-working")) ||
											(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Warehouse")) ||
											(propertyData.transaction_type == "buy" && propertyData.property_type.includes("Factory")) ||
											(propertyData.transaction_type == "rent" &&
												propertyData.property_type.includes("Independent House")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Apartment")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Villa")) ||
											(propertyData.transaction_type == "rent" &&
												propertyData.property_type.includes("Builder Floor Apartment")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Penthouse")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Row House")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Plot")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Farm Plot")) ||
											(propertyData.transaction_type == "rent" &&
												propertyData.property_type.includes("Plot (Commercial)")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Farm House")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Warehouse")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Factory")) ||
											(propertyData.transaction_type == "rent" &&
												propertyData.property_type.includes("Building (Commercial)")) ||
											(propertyData.transaction_type == "rent" &&
												propertyData.property_type.includes("Building (Residential)")) ||
											(propertyData.transaction_type == "rent" && propertyData.property_type.includes("Shop")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Independent House")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Co-living/PG")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Building (Commercial)")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Building (Residential)")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Shop")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Office Space")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Co-working")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Plot")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Farm Plot")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Plot (Commercial)")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Farm House")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Warehouse")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Apartment")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Villa")) ||
											(propertyData.transaction_type == "resale" &&
												propertyData.property_type.includes("Builder Floor Apartment")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Penthouse")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Row House")) ||
											(propertyData.transaction_type == "resale" && propertyData.property_type.includes("Factory"))) &&
										(property_area.super_built_up_area.min.ft === 0 ||
											property_area.super_built_up_area.min.ft === null ||
											property_area.super_built_up_area.max.ft === 0 ||
											property_area.super_built_up_area.max.ft === null ||
											(property_area.super_built_up_area.min.ft !== 0 &&
												property_area.super_built_up_area.max.ft !== 0 &&
												property_area.super_built_up_area.min.ft > property_area.super_built_up_area.max.ft) ||
											isNaN(property_area.super_built_up_area.min.ft) ||
											isNaN(property_area.super_built_up_area.max.ft))
									) {
										if (
											property_area.super_built_up_area.min.ft === 0 ||
											property_area.super_built_up_area.min.ft === null
										) {
											setErrorMinSba(true);
										}
										if (
											property_area.super_built_up_area.max.ft === 0 ||
											property_area.super_built_up_area.max.ft === null
										) {
											setErrorMaxSba(true);
										}
										if (
											(propertyData.property_type.includes("Plot") ||
												propertyData.property_type.includes("Farm Plot") ||
												propertyData.property_type.includes("Plot (Commercial)")) &&
											(property_area.super_built_up_area.min.ft === 0 ||
												property_area.super_built_up_area.min.ft === null ||
												property_area.super_built_up_area.max.ft === 0 ||
												property_area.super_built_up_area.max.ft === null)
										) {
											setErrorSba(true);
										}
										setErrorSba(true);
									}
								} else {
									submitForm();
								}
							}}
							sx={{ marginTop: "1.125rem", width: "100%" }}
							variant="contained"
							disabled={submitDisabled}
						>
							{localLang.update}
						</BeegruButton>
					)}
				</>
			) : null}
		</PaperContainer>
	);
};

export default ConfigAndAreaCard;
